/**
 * ms-search.js — pesquisa por atributos, 100% no browser.
 *
 * O índice (ms-index.json) é carregado uma vez, à primeira interacção com a
 * caixa de pesquisa, e fica em cache no browser. A partir daí não há qualquer
 * pedido de rede: cada pesquisa resolve-se em memória.
 *
 * Ideia central: termos como "a bateria" ou "exterior" não são tratados como
 * texto a procurar, mas como restrições sobre atributos extraídos das
 * descrições. "barra led a bateria" = filtro alimentacao∈{bateria,ambas}
 * + pesquisa lexical por "barra led".
 */
(function () {
  'use strict';

  var PESOS = { titulo: 6, sku: 9, marca: 3, atributo: 4, termos: 2, texto: 1 };
  var MAX_RESULTADOS = 8;
  var DEBOUNCE_MS = 60;

  /* Onde um título deixa de dizer o que o produto é e passa a dizer para que
     serve: "Suporte p/ Coluna", "Cabo para Microfone". */
  var SEPARADOR_FINALIDADE = /\s+(?:p\/|para|compat[íi]vel com)\s+/i;

  /* Um produto cujo TIPO é o que se procura vale mais do que um que apenas o
     menciona. O `tipo` é o atributo mais fiável do índice — vem das tags feitas
     à mão e, na avaliação, saiu certo em 100% dos casos. É empurrão, nunca
     filtro: quem não tem tipo identificado não pode ser excluído por isso. */
  var IMPULSO_TIPO = 2.2;

  /* O que a loja aprende (14/10/2026). Os três vêm do ficheiro de stock do
     bulk, refeito de hora a hora — não esperam pelo PC nem por publicar o tema.

     Multiplicam a relevância em vez de a somar: um cabo que se vende muito sobe
     entre os cabos, mas não passa à frente de uma pergunta sobre colunas. É o
     que separa «o mais vendido do que procuras» de «o mais vendido da loja».

     - vendas: escalão 1..10 (últimos 12 meses, os últimos 90 dias a dobrar).
       No topo vale +90%.
     - cliques: em que produto clicaram os clientes depois de fazerem ESTA
       pesquisa. 1 clique +35%, 3 cliques +70%, a partir de ~15 o máximo, x2,5.
     - novos: criados há menos de 30 dias, +25% — sem vendas nem cliques ainda,
       ficavam enterrados para sempre debaixo dos que já os têm. */
  var PESO_VENDAS = 0.09;
  var PESO_CLIQUES = 0.35;
  var MAX_CLIQUES = 2.5;
  var IMPULSO_NOVO = 1.25;
  var TRAVAO_TIPO = 0.35;

  /* Onde a lista deixa de ser uma resposta e passa a ser uma cauda.
     «coluna» devolvia 683 produtos e o catálogo tem 333 colunas: os primeiros
     200 eram colunas a 100%, e o resto era tudo o que menciona a palavra a
     meio de uma ficha — suportes de coluna, cabos de coluna, um cabo Lightning.
     Pontuavam 2 num topo de 31, mas contavam para o número que se mostra ao
     cliente e para as contagens das facetas.
     Medido sobre 16 perguntas com categoria conhecida: a 15% do melhor
     resultado cortam-se 2148 produtos e não se perde um único do tipo certo.
     Aos 20% já se perdem dois, e por isso o valor é este e não um mais bonito. */
  var FRACAO_CAUDA = 0.15;
  var MINIMO_UTEIS = 24;   // uma grelha cheia: abaixo disto não se corta nada

  /* Nem todo o tipo diferente é uma contradição. Um woofer não é uma coluna,
     mas quem procura "altifalante" quer vê-lo; uma ficha de altifalante, não.
     Os primos não levam empurrão nem travão — ficam onde o texto os puser. */
  var FAMILIA = {
    coluna: ['subwoofer', 'monitor_estudio'],
    subwoofer: ['coluna'],
    monitor_estudio: ['coluna'],
    barra_led: ['par_led'],
    par_led: ['barra_led'],
    maquina_fumo: ['maquina_haze'],
    maquina_haze: ['maquina_fumo'],
    mesa_mistura: ['controlador_dj', 'interface_audio'],
    controlador_dj: ['mesa_mistura', 'cdj'],
    cdj: ['controlador_dj'],
  };

  /* Um filtro por "bateria" também deve aceitar quem funciona com ambas. */
  var EQUIVALENCIAS = {
    alimentacao: { bateria: ['bateria', 'ambas'], fonte: ['fonte', 'ambas'] },
    uso: { exterior: ['exterior', 'ambos'], interior: ['interior', 'ambos'] },
  };

  /* ------------------------------------------------------------- utilitários */

  /* Uma medida escrita de cinco maneiras é cinco palavras diferentes para o
     índice, e era por isso que «cabo xlr 2 metros» devolvia os de 1, 10 e 15
     metros: o «2» caía por ter um só carácter e sobrava «metros», que todos
     eles têm. O catálogo tem 678 títulos com «metros», 432 com «mt», 242 com
     «m», e ainda «metro» e «mts». Aqui passam todos a ser a mesma coisa: 2m.

     O separador decimal também não é pontuação. Sem isto «1,5m» ficava «5m» —
     a mesma palavra que «0,5m» e que «5m» — e quem pedia um cabo de metro e
     meio recebia os de cinco. Fica protegido enquanto os pontos a sério são
     varridos, e volta ao lugar a seguir. É também o que põe as fichas «6,35» e
     «3,5» a existirem como número, em vez de um «35» solto e ambíguo. */
  var DECIMAL = '\u0001';

  function normalizar(s) {
    return (s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/(\d)\s*(?:''|["\u2033])/g, '$1 polegadas')
      .replace(/(\d)[.,](\d)/g, '$1' + DECIMAL + '$2')
      .replace(/[^a-z0-9\s\-/.\u0001]/g, ' ')
      .replace(/\./g, ' ')
      .replace(new RegExp(DECIMAL, 'g'), '.')
      /* «USB-C», «usb c», «type-c»: o «c» sozinho tem uma letra e caía no
         `tokenizar`, por isso «cabo usb c» era só «cabo usb» e os cabos USB-C
         afogavam-se nos 600 artigos com USB. Fica «usb usbc»: quem escreve
         «usb» continua a ver tudo, quem escreve «usb c» vê só os USB-C. */
      // «USB c/ Microfone» é «com», não USB-C: com espaço e barra não conta.
      .replace(/(^|[^a-z0-9])(?:usb|type|tipo)(?:\s*-\s*c|\s*c(?!\s*\/))(?![a-z0-9])/g, '$1usb usbc')
      .replace(/(\d+(?:\.\d+)?)\s*(?:metros|metro|mts|mt|m)(?![a-z0-9])/g, '$1m')
      .replace(/(\d+(?:\.\d+)?)\s*(?:polegadas|polegada|pol)(?![a-z0-9])/g, '$1pol')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /* Palavras que ligam outras palavras e não distinguem produto nenhum.
     Ficam fora do índice e fora da pergunta, para que «mesa de mistura» e
     «mesa mistura» sejam a mesma coisa. Não é uma subtileza: o catálogo
     escreve «Mesa Mistura» em 35 títulos e «Mesa de Mistura» em 9, e quem
     escrevesse a forma com «de» só via esses 9 — o «de» contava como termo,
     e a regra da cobertura total (ver `recolher`) punha fora todos os outros.
     «Cabo Microfone» tem 19 títulos e «Cabo de Microfone» nenhum: quem
     escrevia a pergunta em português correcto não encontrava um único cabo.

     «sem» e «nao» não entram nesta lista, e é de propósito: «sem fios» não
     pode passar a ser «fios». O indexador tem a sua própria lista, mais
     larga, para o texto das descrições (STOPWORDS no build-index.mjs) — esta
     é a mínima que os dois lados têm de partilhar. */
  var LIGACOES = {
    de: 1, do: 1, da: 1, dos: 1, das: 1, ao: 1, aos: 1, no: 1, na: 1, nos: 1,
    nas: 1, em: 1, um: 1, uma: 1, uns: 1, umas: 1, os: 1, as: 1, com: 1,
    para: 1, pra: 1, pelo: 1, pela: 1, ou: 1
  };

  /**
   * A mesma limpeza, mas sobre a frase inteira — para o dicionário e para a
   * pergunta.
   *
   * A primeira palavra nunca cai, e não é um pormenor: à cabeça uma ligação
   * carrega sentido, no meio é só cola. «com ficha» é o filtro de alimentação
   * por corrente; reduzido a «ficha» passava a apanhar as centenas de fichas
   * do catálogo, e «ficha 3,5» deixava de ser uma pergunta sobre fichas para
   * ser um filtro de alimentação. Já «mesa de mistura» é «mesa mistura» sem
   * perder nada.
   */
  function semLigacoes(s) {
    var partes = s.split(' ').filter(Boolean);
    return partes
      .filter(function (t, i) { return i === 0 || !LIGACOES[t]; })
      .join(' ');
  }

  function tokenizar(s) {
    return normalizar(s)
      .split(/[\s\-/]+/)          // o ponto já não parte: os decimais são números
      .filter(function (t) { return t.length >= 2 && !LIGACOES[t]; });
  }

  /** Distância de edição com corte em 1 — sai cedo, não constrói matriz. */
  function distanciaUm(a, b) {
    if (a === b) return 0;
    var la = a.length, lb = b.length;
    if (Math.abs(la - lb) > 1) return 2;
    var i = 0, j = 0, dif = 0;
    while (i < la && j < lb) {
      if (a[i] === b[j]) { i++; j++; continue; }
      if (++dif > 1) return 2;
      if (la > lb) i++;
      else if (lb > la) j++;
      else { i++; j++; }
    }
    if (i < la || j < lb) dif++;
    return dif > 1 ? 2 : dif;
  }

  function escaparHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /**
   * O preço no formato da LOJA, não no do browser.
   *
   * A Shopify está configurada com `€8.264,95` — símbolo à frente, ponto nos
   * milhares, vírgula nos decimais. O `Intl` em pt-PT escreve `8 264,95 €`:
   * outro separador, outro sítio para o símbolo. Enquanto a camada usou o
   * `Intl`, a mesma loja mostrava o preço de duas maneiras na mesma página —
   * daquelas coisas que ninguém sabe nomear e toda a gente nota.
   */
  var SIMBOLOS = { EUR: '€', USD: '$', GBP: '£' };

  function formatarPreco(valor, moeda) {
    var n = Number(valor);
    if (!isFinite(n)) return '';
    var partes = Math.abs(n).toFixed(2).split('.');
    partes[0] = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    var simbolo = SIMBOLOS[moeda || 'EUR'] || '';
    return (n < 0 ? '-' : '') + simbolo + partes[0] + ',' + partes[1];
  }

  /* ------------------------------------------------------------------ motor */

  function Motor(indice) {
    var self0 = this;
    this.docs = indice.produtos;
    this.lerDicionario(indice.sinonimos || {});

    /* As marcas do catálogo, para as reconhecer na query. Quem escreve "udg
       barra led" está a impor uma condição, não a dar uma pista: devolver-lhe
       barras de outra marca é fingir que ele não escreveu "udg". */
    var marcas = Object.create(null);
    this.porMarca = Object.create(null);
    this.docs.forEach(function (doc) {
      var m = normalizar(doc.v);
      if (m.length < 3) return;
      marcas[m] = 1;
      (self0.porMarca[m] = self0.porMarca[m] || []).push(doc);
    });
    this.marcasExactas = marcas;
    this.frasesMarca = Object.keys(marcas).sort(function (a, b) { return b.length - a.length; });

    /* As referências exactas, para a pesquisa por SKU não passar pelo lexical.
       Guardam-se as duas grafias — com e sem traços — porque quem copia de uma
       etiqueta escreve «KM-BM539» e quem escreve de cabeça escreve «KMBM539». */
    this.porSku = Object.create(null);
    this.docs.forEach(function (doc, idx) {
      (doc.s || []).forEach(function (sku) {
        var limpo = normalizar(sku).replace(/\s+/g, '');
        var nu = limpo.replace(/[\-/.]/g, '');
        if (limpo && !(limpo in self0.porSku)) self0.porSku[limpo] = idx;
        if (nu && !(nu in self0.porSku)) self0.porSku[nu] = idx;
      });
    });

    this.lerCliques(indice.cliques || []);
    this.pronto = false;
  }

  /* O índice invertido faz-se à primeira pesquisa, e não ao carregar.
     É a parte cara (mais de um segundo num telemóvel fraco) e a página de
     coleção não precisa dele: só filtra e ordena os documentos. Assim a grelha
     da coleção aparece logo, e o índice faz-se a seguir, com o browser parado
     (ver `montarColecao`), para a lupa abrir sem esperar. */
  Motor.prototype.preparar = function () {
    if (this.pronto) return;
    this.pronto = true;
    this.construirPostings();
    this.apelidarMarcas();
    this.despromoverMarcas();
  };

  /* A chave de uma pesquisa para os cliques: as palavras, sem ordem nem
     repetidas. «cabo usb c» e «usb c cabo» são a mesma pergunta. */
  function chaveCliques(q) {
    return tokenizar(q)
      .filter(function (t, i, a) { return a.indexOf(t) === i; })
      .sort().join(' ');
  }

  /** [[pesquisa, handle, vezes]] -> {chave: {índice do produto: vezes}}. */
  Motor.prototype.lerCliques = function (lista) {
    var porHandle = Object.create(null);
    this.docs.forEach(function (d, i) { porHandle[d.h] = i; });
    var mapa = Object.create(null);
    lista.forEach(function (c) {
      var idx = porHandle[c[1]];
      var k = chaveCliques(c[0]);
      if (idx === undefined || !k) return;
      var m = mapa[k] || (mapa[k] = Object.create(null));
      m[idx] = (m[idx] || 0) + (c[2] || 1);
    });
    this.cliques = mapa;
  };

  /**
   * Tudo o que vem do sinonimos.json, e mais nada.
   *
   * Está à parte do construtor porque o afinador precisa de experimentar uma
   * entrada nova e medir o que ela faz. Reconstruir o índice invertido por
   * cada palavra candidata tornava a medição cara ao ponto de não se fazer —
   * e um sinónimo que ninguém mede é um sinónimo em que ninguém confia.
   */
  Motor.prototype.lerDicionario = function (sin) {
    var self0 = this;
    this.filtrosDic = sin.filtros || [];
    this.expansoes = sin.expansoes || {};

    // Frases de tipo, das mais longas para as mais curtas: "mesa de mistura"
    // tem de ser reconhecida antes de "mesa".
    var tipos = sin.tipos || {};
    this.frasesTipo = Object.keys(tipos)
      .map(function (t) { return { termo: semLigacoes(normalizar(t)), tipo: tipos[t] }; })
      .sort(function (a, b) { return b.termo.length - a.termo.length; });

    /* Frases que a loja não usa e o cliente sim: «caixa de som» (Brasil),
       «ficha p10», «jack 1/4». Trocadas na pergunta ANTES de tudo o resto,
       para poderem activar o tipo e os filtros como se o cliente tivesse
       escrito a palavra do catálogo.

       Tinham de ser uma secção à parte: as `expansoes` são consultadas por
       palavra solta (ver `expandir`), por isso uma entrada de duas palavras
       nunca lá era encontrada — as que existiam eram letra morta.

       Uma passagem só, da frase mais longa para a mais curta. O destino de
       uma frase não deve ser a origem de outra: seria uma cadeia, e o
       resultado passava a depender da ordem. Quem escreve o dicionário —
       pessoa ou sugeridor — evita isso. */
    var frasesDic = sin.frases || {};
    this.frases = Object.keys(frasesDic)
      .map(function (f) {
        return { de: semLigacoes(normalizar(f)), para: semLigacoes(normalizar(frasesDic[f])) };
      })
      .filter(function (f) { return f.de && f.para && f.de !== f.para; })
      .sort(function (a, b) { return b.de.length - a.de.length; });

    // Frases de filtro ordenadas por comprimento: "sem fios" antes de "fios".
    this.frasesFiltro = this.filtrosDic
      .reduce(function (acc, f) {
        f.termos.forEach(function (t) {
          var termo = semLigacoes(normalizar(t));
          /* «usb» sozinho é quase sempre a ficha (cabo usb, usb c), não a forma
             de alimentar: como filtro deixava de fora os cabos todos. Fica só
             «alimentado por usb». */
          if (termo === 'usb' || termo === 'usb usbc') return;
          acc.push({ termo: termo, filtro: f });
        });
        return acc;
      }, [])
      .sort(function (a, b) { return b.termo.length - a.termo.length; });


    /* Rótulos legíveis para os códigos de tipo: `mesa_mistura` -> «Mesa de
       mistura». O primeiro sinónimo de cada tipo é escrito por uma pessoa e é
       melhor rótulo do que qualquer coisa que se invente a partir do código. */
    this.rotulos = Object.create(null);
    Object.keys(tipos).forEach(function (termo) {
      if (!self0.rotulos[tipos[termo]]) self0.rotulos[tipos[termo]] = termo;
    });
  };

  /**
   * "Udg Gear" é escrita "udg" por quem procura. Mas "Music Stage" não pode
   * transformar "music" numa marca, ou qualquer pergunta com essa palavra
   * passava a ser uma pergunta sobre uma marca.
   *
   * Quem decide são os dados: a primeira palavra do nome só vira apelido se a
   * esmagadora maioria dos produtos onde ela aparece for mesmo dessa marca.
   */
  Motor.prototype.apelidarMarcas = function () {
    var self = this;
    Object.keys(this.marcasExactas).forEach(function (marca) {
      var partes = marca.split(' ');
      if (partes.length < 2) return;

      var apelido = partes[0];
      if (apelido.length < 3 || self.marcasExactas[apelido]) return;

      var lista = self.postings[apelido];
      if (!lista) return;

      var total = 0, dela = 0;
      for (var idx in lista) {
        total++;
        if (normalizar(self.docs[idx].v) === marca) dela++;
      }
      if (total >= 3 && dela / total >= 0.75) {
        self.marcasExactas[apelido] = marca;
        self.frasesMarca.push(apelido);
      }
    });
    this.frasesMarca.sort(function (a, b) { return b.length - a.length; });
  };

  /* Uma marca com um ou dois produtos não pode calar uma palavra que o catálogo
     usa em dezenas de fichas. */
  var MARCA_MINIMA = 2;        // até aqui, a marca não tem catálogo que se veja
  var MARCA_RUIDO = 20;        // e a palavra anda por aí tantas vezes mais

  /**
   * Tira o poder de condição às marcas que são, na verdade, uma palavra comum.
   *
   * Há no catálogo uma ponte rectificadora com a marca preenchida «Microfone» e
   * outra com «MIC» — erro de importação do fornecedor. Como a marca é condição
   * rígida, procurar «microfone» numa loja de som devolvia essa ponte e mais
   * nada: uma linha mal preenchida apagava 800 fichas. Dados maus vão existir
   * sempre; o que não pode existir é uma delas com esse poder.
   *
   * O teste é estreito de propósito. Não chega a palavra aparecer em produtos
   * de outras marcas — a Samsung tem três artigos e é mencionada em trinta
   * fichas de adaptadores, e continua a ser uma marca a sério. O que denuncia o
   * erro é a desproporção: quase nenhum produto próprio, e a palavra por todo o
   * lado.
   *
   * Despromover não é ignorar: a palavra continua a pontuar como marca na
   * pesquisa lexical, por isso quem procura «microfone» vê na mesma os
   * microfones em primeiro. Deixa é de mandar calar tudo o resto.
   */
  Motor.prototype.despromoverMarcas = function () {
    var self = this;
    this.marcasDespromovidas = [];
    this.frasesMarca = this.frasesMarca.filter(function (marca) {
      // Os apelidos apontam para o nome verdadeiro: "udg" -> "udg gear", e é o
      // catálogo do nome verdadeiro que conta.
      var alvo = self.marcasExactas[marca];
      var nome = typeof alvo === 'string' ? alvo : marca;
      var proprios = (self.porMarca[nome] || []).length;
      if (proprios > MARCA_MINIMA) return true;

      var lista = self.postings[marca];
      if (!lista) return true;
      var total = 0;
      for (var idx in lista) total++;
      if (total < proprios * MARCA_RUIDO) return true;

      delete self.marcasExactas[marca];
      self.marcasDespromovidas.push(marca);
      return false;
    });
  };

  /** O rótulo legível de um código de tipo, para as facetas. */
  Motor.prototype.rotuloTipo = function (tipo) {
    var r = (this.rotulos && this.rotulos[tipo]) || String(tipo || '').replace(/_/g, ' ');
    return r.charAt(0).toUpperCase() + r.slice(1);
  };

  Motor.prototype.construirPostings = function () {
    var postings = Object.create(null);

    function juntar(token, docIdx, peso) {
      var lista = postings[token];
      if (!lista) { postings[token] = lista = Object.create(null); }
      lista[docIdx] = (lista[docIdx] || 0) + peso;
    }

    /* Sem isto, "LED SMD3030 p/ Barra LED" conta "led" duas vezes e passa à
       frente da barra de LED verdadeira. Repetir uma palavra no título não
       torna o produto mais relevante. */
    function unicos(lista) {
      var vistos = Object.create(null), saida = [];
      for (var i = 0; i < lista.length; i++) {
        if (!vistos[lista[i]]) { vistos[lista[i]] = 1; saida.push(lista[i]); }
      }
      return saida;
    }

    this.docs.forEach(function (doc, idx) {
      /* "Líquido p/ Máquina de Fumo" não é uma máquina de fumo; "Pé Borracha
         p/ Colunas" não é uma coluna. O que vem depois de "p/" ou "para" é
         aquilo para que o produto serve — conta, mas não como se fosse a
         identidade do artigo. São 1077 produtos no catálogo, e eram eles que
         apareciam à frente do produto verdadeiro. */
      var partido = doc.t.split(SEPARADOR_FINALIDADE);
      unicos(tokenizar(partido[0])).forEach(function (t) { juntar(t, idx, PESOS.titulo); });
      if (partido.length > 1) {
        unicos(tokenizar(partido.slice(1).join(' '))).forEach(function (t) {
          juntar(t, idx, PESOS.titulo * 0.25);
        });
      }
      unicos(tokenizar(doc.v)).forEach(function (t) { juntar(t, idx, PESOS.marca); });
      /* Os SKUs todos de uma vez, e cada token contado UMA vez.
       *
       * Estavam a ser contados por SKU: os «Perfumes» têm dez variantes,
       * KM-PARFUM-1 a 10, e o token «km» ficava com dez vezes o peso — 90
       * contra os 9 de quem tem um SKU só. Procurar «KM-BM539» devolvia os
       * Perfumes em primeiro e o artigo certo em segundo: ter muitas variantes
       * valia mais do que ser a referência pedida. */
      var tokensSku = [];
      (doc.s || []).forEach(function (sku) {
        tokensSku = tokensSku.concat(tokenizar(sku));
        var inteiro = normalizar(sku).replace(/[\s\-/.]/g, '');
        if (inteiro) tokensSku.push(inteiro);
      });
      unicos(tokensSku).forEach(function (t) { juntar(t, idx, PESOS.sku); });
      Object.keys(doc.a || {}).forEach(function (chave) {
        var v = doc.a[chave];
        var valores = Array.isArray(v) ? v : [v];
        valores.forEach(function (val) {
          if (typeof val === 'string') {
            tokenizar(val.replace(/_/g, ' ')).forEach(function (t) { juntar(t, idx, PESOS.atributo); });
          }
        });
      });
      (doc.x || []).forEach(function (t) { juntar(t, idx, PESOS.texto); });
      /* Os termos que a IA escreveu (`k`, ver gerar-termos.mjs): como os clientes
         chamam ao produto — «cabo type c», «armário rack», «neve para natal».
         Peso baixo, e só as palavras que o título ainda não tem: servem para
         ENCONTRAR o produto, não para o pôr à frente de quem já diz o mesmo no
         título. Sem esta regra os produtos já enriquecidos ganhavam a todos os
         outros só por terem sido enriquecidos primeiro. */
      if (doc.k) {
        var doTitulo = Object.create(null);
        tokenizar(doc.t).forEach(function (t) { doTitulo[t] = 1; });
        unicos(tokenizar(doc.k.replace(/\|/g, ' '))).forEach(function (t) {
          if (!doTitulo[t]) juntar(t, idx, PESOS.termos);
        });
      }
    });

    this.postings = postings;
    this.tokens = Object.keys(postings).sort();
  };

  /** Separa a query em filtros rígidos + termos lexicais. */
  Motor.prototype.interpretar = function (query) {
    var pergunta = semLigacoes(normalizar(query));
    for (var f = 0; f < this.frases.length; f++) {
      var alvoFrase = ' ' + this.frases[f].de + ' ';
      var onde2 = (' ' + pergunta + ' ').indexOf(alvoFrase);
      if (onde2 !== -1) {
        pergunta = (' ' + pergunta + ' ').split(alvoFrase).join(' ' + this.frases[f].para + ' ').trim();
      }
    }

    var restante = ' ' + pergunta + ' ';
    var filtros = [];
    var consumidas = [];

    this.frasesFiltro.forEach(function (entrada) {
      var alvo = ' ' + entrada.termo + ' ';
      if (restante.indexOf(alvo) !== -1) {
        restante = restante.split(alvo).join(' ');
        consumidas.push(entrada.termo);
        var f = entrada.filtro;
        var palavras = tokenizar(entrada.termo);
        var jaTem = filtros.filter(function (x) { return x.atributo === f.atributo && x.valor === f.valor; })[0];
        if (!jaTem) filtros.push({ atributo: f.atributo, valor: f.valor, palavras: palavras });
        else jaTem.palavras = jaTem.palavras.concat(palavras);
      }
    });

    // As palavras que viraram filtro saem da pesquisa lexical, mas não devem
    // desaparecer da ordenação: em "coluna portátil a bateria" sobrava só
    // "coluna", e as 44 colunas do catálogo ficavam todas empatadas. Voltam
    // como reforço de peso baixo — quem diz "Portátil" e "Bateria" no título é
    // melhor resposta do que quem apenas o é.
    var reforco = [];
    consumidas.forEach(function (frase) {
      tokenizar(frase).forEach(function (t) {
        if (reforco.indexOf(t) === -1) reforco.push(t);
      });
    });

    // O que o cliente está a procurar. Ao contrário dos filtros, isto não
    // consome as palavras: "mesa de mistura" continua a ser pesquisada como
    // texto — só que quem É uma mesa de mistura passa à frente de quem apenas
    // tem as palavras no título, como as malas de transporte.
    var procurado = null;
    var normalizada = ' ' + pergunta + ' ';
    /* Havendo mais do que um tipo na pergunta, ganha o que vem primeiro, não
       o que tiver o nome mais comprido. Em português o que se procura está à
       cabeça e o resto é finalidade: «cabo de microfone» é um cabo, «mala para
       mesa de mistura» é uma mala. Sem isto, «microfone» ganhava a «cabo» por
       ter mais letras e a pergunta devolvia microfones.

       A comparação é estrita e a lista continua ordenada da frase mais longa
       para a mais curta, para «mesa de mistura» não perder para «mesa», que
       começa na mesma posição. */
    var posicao = Infinity;
    for (var i = 0; i < this.frasesTipo.length; i++) {
      var onde = normalizada.indexOf(' ' + this.frasesTipo[i].termo + ' ');
      if (onde !== -1 && onde < posicao) {
        posicao = onde;
        procurado = this.frasesTipo[i].tipo;
      }
    }

    // A marca, se lá estiver, é condição — não sugestão.
    var marca = null;
    for (var k = 0; k < this.frasesMarca.length; k++) {
      if ((' ' + normalizar(restante) + ' ').indexOf(' ' + this.frasesMarca[k] + ' ') !== -1) {
        marca = this.frasesMarca[k];
        break;
      }
    }

    return { filtros: filtros, termos: tokenizar(restante), reforco: reforco, tipo: procurado, marca: marca };
  };

  /**
   * Plurais portugueses, sem tabela de sinónimos. Cobre o caso corrente de o
   * cliente escrever "luzes"/"colunas" e a ficha dizer "luz"/"coluna" (e o
   * inverso). Não é um stemmer: devolve variantes candidatas e quem decide é o
   * índice — se a variante não existir, não pontua.
   */
  function variantesNumero(t) {
    if (t.length < 4 || !/^[a-z]+$/.test(t)) return [];
    var v = [];
    if (/oes$/.test(t)) v.push(t.slice(0, -3) + 'ao');       // iluminacoes -> iluminacao
    else if (/(ais|eis|ois|uis)$/.test(t)) v.push(t.slice(0, -2) + 'l'); // sinais -> sinal
    else if (/ns$/.test(t)) v.push(t.slice(0, -2) + 'm');    // armazens -> armazem
    else if (/(z|r|s)es$/.test(t)) v.push(t.slice(0, -2));   // luzes -> luz, cores -> cor
    else if (/s$/.test(t)) v.push(t.slice(0, -1));           // cabos -> cabo
    else {                                                    // singular escrito, plural no índice
      v.push(t + 's');
      if (/ao$/.test(t)) v.push(t.slice(0, -2) + 'oes');
      if (/[lr]$/.test(t)) v.push(t + 'es');
    }
    return v;
  }

  /**
   * Cada entrada leva a marca do termo original que a gerou (`origem`), para o
   * ranking saber quantos termos da query um produto cobre de facto.
   */
  Motor.prototype.expandir = function (termos) {
    var self = this, saida = [];
    termos.forEach(function (t, origem) {
      saida.push({ token: t, peso: 1, origem: origem });

      var variantes = variantesNumero(t);
      variantes.forEach(function (v) {
        saida.push({ token: v, peso: 0.9, origem: origem });
      });

      // O dicionário está escrito no singular: "colunas" tem de encontrar a
      // entrada "coluna".
      [t].concat(variantes).forEach(function (chave) {
        var alt = self.expansoes[chave];
        if (!alt) return;
        alt.forEach(function (a) {
          tokenizar(a).forEach(function (tk) {
            if (tk !== t) saida.push({ token: tk, peso: 0.7, origem: origem });
          });
        });
      });
    });
    return saida;
  };

  /** Candidatos por prefixo, para o termo que o utilizador ainda está a escrever. */
  Motor.prototype.porPrefixo = function (prefixo, limite) {
    var tokens = this.tokens, lo = 0, hi = tokens.length;
    while (lo < hi) {
      var meio = (lo + hi) >> 1;
      if (tokens[meio] < prefixo) lo = meio + 1; else hi = meio;
    }
    var saida = [];
    for (var i = lo; i < tokens.length && tokens[i].indexOf(prefixo) === 0 && saida.length < limite; i++) {
      saida.push(tokens[i]);
    }
    return saida;
  };

  Motor.prototype.porGralha = function (termo) {
    var saida = [];
    for (var i = 0; i < this.tokens.length; i++) {
      var t = this.tokens[i];
      if (Math.abs(t.length - termo.length) > 1) continue;
      if (distanciaUm(t, termo) <= 1) saida.push(t);
      if (saida.length >= 6) break;
    }
    return saida;
  };

  /* Quem diz a palavra do filtro no título (ou nos termos da IA) passa o filtro,
     mesmo sem o atributo. «Tripé» e «rack» são filtros de montagem — «montável em
     tripé» — e «tripé celular» deixava de fora o próprio tripé, que não se monta
     em tripé nenhum: é um. O mesmo com «armário rack» e o bastidor. (14/10/2026) */
  Motor.prototype.dizNoTexto = function (doc, palavras) {
    if (!palavras || !palavras.length) return false;
    if (!doc._txt) {
      doc._txt = Object.create(null);
      tokenizar(doc.t + ' ' + (doc.k || '').replace(/\|/g, ' ')).forEach(function (t) { doc._txt[t] = 1; });
    }
    return palavras.every(function (t) { return doc._txt[t]; });
  };

  Motor.prototype.passaFiltros = function (doc, filtros, aceitarDesconhecido) {
    for (var i = 0; i < filtros.length; i++) {
      var f = filtros[i];
      var valorDoc = (doc.a || {})[f.atributo];
      if (this.dizNoTexto(doc, f.palavras)) continue;

      if (valorDoc === undefined || valorDoc === null) {
        if (aceitarDesconhecido) continue;
        return false;
      }

      var aceites = [f.valor];
      var eq = EQUIVALENCIAS[f.atributo];
      if (eq && eq[f.valor]) aceites = eq[f.valor];

      var ok = Array.isArray(valorDoc)
        ? valorDoc.some(function (v) { return aceites.indexOf(v) !== -1; })
        : aceites.indexOf(valorDoc) !== -1;

      if (!ok) return false;
    }
    return true;
  };

  /**
   * `opcoes.limite` é quantos resultados devolver. O dropdown pedia oito; a
   * camada pede tudo, porque precisa da lista inteira para contar as facetas —
   * dizer «Marca: Fos (37)» a partir de uma amostra de oito era mentir.
   */
  Motor.prototype.pesquisar = function (query, opcoes) {
    this.preparar();
    var limite = (opcoes && opcoes.limite) || MAX_RESULTADOS;

    /* Uma referência é uma pergunta fechada.
     *
     * Quem escreve «KM-BM539» quer aquele artigo — não os 819 que partilham o
     * «KM» do prefixo da marca. Pela via lexical o prefixo é um token como
     * outro qualquer e arrasta o catálogo inteiro atrás dele: a lista ficava
     * com 819 linhas e parecia que a pesquisa não tinha filtrado nada.
     *
     * Só vale para o SKU inteiro e exacto. Meia referência continua a ser uma
     * pesquisa normal, porque aí quem escreve ainda está à procura. */
    var exacto = this.porSku[normalizar(query).replace(/\s+/g, '')];
    if (exacto !== undefined) {
      return {
        resultados: [{ doc: this.docs[exacto], score: 1000, cobertos: 1 }],
        total: 1, filtros: [], parcial: false, marcaIgnorada: null,
      };
    }

    var clicados = this.cliques[chaveCliques(query)] || null;
    var interpretacao = this.interpretar(query);
    var filtros = interpretacao.filtros;
    var termos = interpretacao.termos;
    var tipoProcurado = interpretacao.tipo;
    var marcaPedida = interpretacao.marca;

    if (!termos.length && !filtros.length) {
      return { resultados: [], total: 0, filtros: filtros, parcial: false };
    }

    var pontos = Object.create(null);
    var cobertura = Object.create(null);   // bitmask dos termos que cada doc cobre
    var self = this;
    var ultimo = termos.length ? termos[termos.length - 1] : null;

    var entradas = this.expandir(termos).concat(
      (interpretacao.reforco || []).map(function (t) {
        return { token: t, peso: 0.45, origem: -1 };
      })
    );

    /* Um mesmo token do índice pode ser alcançado por vários caminhos — a
       variante de plural e a expansão por prefixo chegam ambas a "colunas".
       Somar os dois dava peso a dobrar a quem tem o plural no título. Aqui
       fica-se pelo melhor caminho, e as origens juntam-se. */
    var alcance = Object.create(null);

    function alcancar(token, peso, origem) {
      var actual = alcance[token];
      if (!actual) { alcance[token] = { peso: peso, marca: origem >= 0 ? 1 << origem : 0 }; return; }
      if (peso > actual.peso) actual.peso = peso;
      if (origem >= 0) actual.marca |= 1 << origem;
    }

    entradas.forEach(function (entrada) {
      var achou = false;
      if (self.postings[entrada.token]) {
        alcancar(entrada.token, entrada.peso, entrada.origem);
        achou = true;
      }

      if (entrada.token === ultimo) {
        self.porPrefixo(entrada.token, 12).forEach(function (t) {
          if (t !== entrada.token) { alcancar(t, entrada.peso * 0.8, entrada.origem); achou = true; }
        });
      }
      if (!achou && entrada.token.length >= 5) {
        self.porGralha(entrada.token).forEach(function (t) {
          alcancar(t, entrada.peso * 0.5, entrada.origem);
        });
      }
    });

    Object.keys(alcance).forEach(function (token) {
      var lista = self.postings[token];
      if (!lista) return;
      var a = alcance[token];
      for (var idx in lista) {
        pontos[idx] = (pontos[idx] || 0) + lista[idx] * a.peso;
        cobertura[idx] = (cobertura[idx] || 0) | a.marca;
      }
    });

    // Só filtros, sem termos: parte-se de todos os produtos.
    if (!termos.length) {
      for (var i = 0; i < this.docs.length; i++) pontos[i] = 1;
    }

    var self2 = this;
    var totalTermos = termos.length;

    function contarBits(n) {
      var c = 0;
      while (n) { n &= n - 1; c++; }
      return c;
    }

    function recolher(aceitarDesconhecido) {
      var saida = [];
      for (var idx in pontos) {
        var doc = self2.docs[idx];
        if (!self2.passaFiltros(doc, filtros, aceitarDesconhecido)) continue;
        var cobertos = contarBits(cobertura[idx] || 0);
        // Corresponder a metade das palavras vale menos de metade: sem isto,
        // "barra led a bateria" devolve a coluna portátil, que tem LEDs e
        // bateria mas não é barra nenhuma.
        var fator = totalTermos ? Math.pow(cobertos / totalTermos, 2) : 1;
        /* Ser aquilo que se procura vale mais do que mencioná-lo — e ser
           outra coisa vale menos. Uma mala de transporte para mesas de mistura
           tem "mesa de mistura" no título e não é uma; o que a desmascara é
           ter tipo próprio (flight_case). Quem não tem tipo identificado fica
           intacto: isto ordena, nunca exclui. */
        var tipoDoDoc = (doc.a || {}).tipo;
        if (tipoProcurado && tipoDoDoc && tipoDoDoc !== tipoProcurado) {
          var primos = FAMILIA[tipoProcurado] || [];
          if (primos.indexOf(tipoDoDoc) === -1) fator *= TRAVAO_TIPO;
        } else if (tipoProcurado && tipoDoDoc === tipoProcurado) {
          fator *= IMPULSO_TIPO;
        }
        /* O que se vende desempata, e nada mais do que isso. `u` é um escalão
           de 1 a 5, já achatado pelo indexador — o índice é público e não leva
           números de vendas em bruto. Se as vendas pesassem a sério, o que já
           está no topo ficava lá para sempre e um produto novo nunca teria
           hipótese de ser descoberto. */
        var escalao = doc.uv !== undefined ? doc.uv : (doc.u || 0) * 2;
        fator *= 1 + escalao * PESO_VENDAS;
        if (clicados && clicados[idx]) {
          fator *= Math.min(MAX_CLIQUES, 1 + PESO_CLIQUES * Math.log2(1 + clicados[idx]));
        }
        if (doc.nv) fator *= IMPULSO_NOVO;
        var score = pontos[idx] * fator + (doc.d ? 1.5 : 0) + filtros.length * 2;
        saida.push({ doc: doc, score: score, cobertos: cobertos });
      }
      saida.sort(function (a, b) { return b.score - a.score; });
      return saida;
    }

    var estritos = recolher(false);
    var parcial = false;
    var marcaIgnorada = null;

    /* A marca é condição rígida. Não havendo nada dessa marca, o honesto é
       dizê-lo e mostrar as alternativas — não empurrar outra marca em silêncio
       como se fosse o que se pediu. */
    if (marcaPedida) {
      // O apelido aponta para o nome verdadeiro: "udg" -> "udg gear".
      var alvo = this.marcasExactas[marcaPedida];
      var nome = typeof alvo === 'string' ? alvo : marcaPedida;
      var daMarca = estritos.filter(function (r) { return normalizar(r.doc.v) === nome; });
      if (daMarca.length) estritos = daMarca;
      else {
        // Mostrar o nome como está no catálogo, não o apelido escrito à pressa.
        var exemplo = (this.porMarca[nome] || [])[0];
        marcaIgnorada = exemplo ? exemplo.v : marcaPedida;
      }
    }

    // Poucos resultados com o filtro rígido? Volta a passar aceitando produtos
    // cujo atributo não foi identificado, e assinala isso ao utilizador.
    if (filtros.length && estritos.length < 3) {
      var largos = recolher(true);
      if (largos.length > estritos.length) { estritos = largos; parcial = true; }
    }

    // Quem cobre a query toda passa à frente — desde que sobre lista que chegue.
    if (totalTermos >= 2) {
      var completos = estritos.filter(function (r) { return r.cobertos === totalTermos; });
      if (completos.length >= 3) estritos = completos;
    }

    /* A cauda fora. Não é só cosmética do número: as contagens das facetas
       saem desta lista, e o «mostrar mais» acaba por lá chegar. O mínimo
       existe para uma pergunta certeira com poucos resultados não ficar
       reduzida a um punhado — aí não há cauda nenhuma para cortar. */
    if (estritos.length > MINIMO_UTEIS) {
      var limiar = estritos[0].score * FRACAO_CAUDA;
      var uteis = estritos.filter(function (r) { return r.score >= limiar; });
      if (uteis.length >= MINIMO_UTEIS) estritos = uteis;
    }

    return {
      resultados: limite >= estritos.length ? estritos : estritos.slice(0, limite),
      total: estritos.length,
      filtros: filtros,
      parcial: parcial,
      marcaIgnorada: marcaIgnorada,
    };
  };

  /* --------------------------------------------------------------------- ui */

  /**
   * A camada: a pesquisa ocupa o ecrã todo, com facetas e grelha.
   *
   * Porquê ecrã inteiro e não um dropdown maior: o que faz uma pesquisa parecer
   * boa não é ver oito produtos mais depressa, é poder fechar o cerco — «destes
   * 842 microfones, só os sem fios, só da Karma, só até 150 €». Isso precisa de
   * espaço e de contagens, e um painel pendurado debaixo da caixa não tem nem
   * uma coisa nem outra.
   *
   * Tudo acontece em memória. O índice já cá está, cada pesquisa custa 2 ms e
   * cada clique numa faceta é recontar um array — não há pedido de rede nenhum
   * entre abrir a camada e chegar à ficha do produto.
   */

  /* O cabeçalho da loja, por omissão. No Xtra é este grupo de secções; o
     `header` é a rede para qualquer outro tema. Sobrepõe-se com `cabecalho` na
     configuração do snippet, sem mexer aqui. */
  var SELETOR_CABECALHO = '.shopify-section-group-header-group, header';

  var PAGINA = 48;              // cartões por carregamento
  var MAX_BASE = 3000;          // tecto do que se traz do motor para a camada
  var VISIVEIS = 6;             // valores mostrados antes do «ver mais»

  /* Um carrinho desenhado, nao uma letra. Fica dentro do botao e herda a cor,
     por isso nao precisa de variante para o estado ligado. */
  var ICONE_CARRINHO =
    '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">' +
    '<path d="M3 4h2l2.4 10.4a2 2 0 0 0 2 1.6h7.2a2 2 0 0 0 2-1.5L20.5 8H6.2" ' +
    'fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" ' +
    'stroke-linejoin="round"/>' +
    '<circle cx="10" cy="19" r="1.4" fill="currentColor"/>' +
    '<circle cx="17" cy="19" r="1.4" fill="currentColor"/></svg>';
  var DESCONTO_MINIMO = 1;      // euros: abaixo disto a etiqueta nao vale a tinta

  /* As facetas, por ordem de aparição. `bandeira` é um atributo que só existe
     ou não existe (portátil, sem fios) e por isso é uma caixa e não uma lista. */
  var FACETAS = [
    { chave: 'tipo', rotulo: 'Categoria' },
    { chave: 'alimentacao', rotulo: 'Alimentação' },
    { chave: 'controlo', rotulo: 'Controlo' },
    { chave: 'montagem', rotulo: 'Montagem' },
    { chave: 'uso', rotulo: 'Uso' },
    { chave: 'ip_rating', rotulo: 'Proteção IP' },
    { chave: 'sem_fios', rotulo: 'Sem fios', bandeira: true },
    { chave: 'portatil', rotulo: 'Portátil', bandeira: true },
  ];

  var ORDENS = [
    { valor: 'relevancia', rotulo: 'Relevância' },
    { valor: 'preco-asc', rotulo: 'Preço: mais barato' },
    { valor: 'preco-desc', rotulo: 'Preço: mais caro' },
    { valor: 'nome', rotulo: 'Nome A-Z' },
  ];

  /* Os rótulos de valores que ficam feios quando se troca o underscore por um
     espaço. O resto resolve-se sozinho. */
  var ROTULOS = {
    dmx: 'DMX', ir: 'Infravermelhos', usb: 'USB', phantom: 'Phantom 48V',
    som: 'Som', automatico: 'Automático', manual: 'Manual', bluetooth: 'Bluetooth',
    gancho_truss: 'Gancho de truss', flight_case: 'Flight case',
    tripe: 'Tripé', encastrar: 'Encastrar', ambas: 'Ambas', ambos: 'Ambos',
  };

  function bonito(valor) {
    var v = String(valor);
    if (ROTULOS[v]) return ROTULOS[v];
    if (/^ip\d+$/i.test(v)) return v.toUpperCase();
    v = v.replace(/_/g, ' ');
    return v.charAt(0).toUpperCase() + v.slice(1);
  }

  /* A imagem do índice vem a 160 px, que chega para uma linha de dropdown e não
     chega para um cartão numa grelha. */
  function imagemGrande(url) {
    return String(url || '').replace(/([?&]width=)\d+/, '$1320');
  }

  /**
   * A promessa de entrega, tal como a loja a faz nos cartões dela.
   *
   * Quem sabe as regras — o corte das 16:30, os dias úteis, os feriados — é o
   * `bukprod-custom.js`, que as aplica aos cartões do tema. Aqui só se lhe
   * pergunta. Copiar as regras para cá era garantir que um dia a loja prometia
   * «amanhã» num sítio e «quinta» no outro, na mesma página.
   *
   * Devolve string vazia se aquele ficheiro não estiver carregado. A camada não
   * pode depender dele: sem promessa, o cartão diz só que há stock.
   */
  function promessaDeEntrega() {
    try {
      if (typeof window.BUKPROD_ENTREGA !== 'function') return '';
      return window.BUKPROD_ENTREGA() || '';
    } catch (e) {
      return '';
    }
  }

  /* Onde acaba o cabeçalho da loja, em píxeis.
   *
   * A camada abre por baixo dele em vez de o tapar. Tapado, a loja desaparecia
   * e aquilo passava a parecer outro site: sem logótipo, sem menu, sem carrinho,
   * e sem forma de sair a não ser um botão nosso. Por baixo, continua a ser a
   * mesma página — e a caixa de pesquisa onde a pessoa acabou de carregar fica
   * onde estava, em vez de saltar para outro sítio.
   *
   * Mede-se no momento de abrir e não uma vez só: o cabeçalho encolhe quando a
   * página desce, e a barra de anúncios desaparece. O que interessa é onde ele
   * está agora.
   */
  function fundoDoCabecalho(seletor) {
    var alto = 0;
    var caixas = document.querySelectorAll(seletor || SELETOR_CABECALHO);
    for (var i = 0; i < caixas.length; i++) {
      var r = caixas[i].getBoundingClientRect();
      // Só o que está encostado ao topo. Um <header> de rodapé de secção, ou
      // um bloco que já saiu do ecrã, não fazem parte da conta.
      if (r.height > 0 && r.top < 120 && r.bottom > alto) alto = r.bottom;
    }
    return Math.max(0, Math.round(alto));
  }

  /* O fundo da faixa do cabeçalho onde está a caixa de pesquisa: o primeiro
     antepassado da caixa que ocupa a largura do ecrã. Só no computador e só com
     a caixa à vista; de resto, 0 e manda o `fundoDoCabecalho`. */
  function linhaDaCaixa(el) {
    if (!el || window.innerWidth < 761) return 0;
    var r = el.getBoundingClientRect();
    if (r.width <= 0 || r.bottom <= 0) return 0;
    for (var n = el.parentElement; n && n !== document.body; n = n.parentElement) {
      var c = n.getBoundingClientRect();
      if (c.width >= window.innerWidth * 0.9) return Math.round(c.bottom);
    }
    return 0;
  }

  /* A caixa de pesquisa do tema so manda se estiver a vista ACIMA da camada.
   *
   * So medir a geometria nao chega: no telemovel a caixa do tema vive dentro
   * da gaveta do menu, que fica POR BAIXO do nosso topo. Ela tem largura,
   * tem altura e esta dentro do ecra -- e esta tapada por nos, que somos
   * z-index 10000. A conta antiga dava-a por visivel, a nossa caixa escondia-se
   * para lhe dar lugar, e ficava-se sem sitio nenhum para escrever.
   *
   * `topoDaCamada` e o mesmo numero que vai para o `style.top`: tudo o que
   * acabe abaixo dessa linha esta debaixo da camada, por definicao. */
  function mandaACaixaDoTema(el, topoDaCamada) {
    if (!el) return false;
    var r = el.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return false;
    if (r.bottom <= 0 || r.top >= window.innerHeight) return false;
    return r.bottom <= topoDaCamada + 2;
  }

  function valoresDe(doc, chave) {
    var v = (doc.a || {})[chave];
    if (v === undefined || v === null) return [];
    return Array.isArray(v) ? v : [v];
  }

  function contar(lista, ler) {
    var conta = Object.create(null);
    for (var i = 0; i < lista.length; i++) {
      var vals = ler(lista[i].doc);
      for (var j = 0; j < vals.length; j++) {
        var v = String(vals[j]);
        conta[v] = (conta[v] || 0) + 1;
      }
    }
    return Object.keys(conta)
      .map(function (v) { return { valor: v, n: conta[v] }; })
      .sort(function (a, b) { return b.n - a.n; });
  }

  /**
   * As marcas contam-se pelo nome normalizado, não pelo que está escrito.
   *
   * O catálogo traz «Glemm» e «GLEMM» como fornecedores diferentes, e a faceta
   * mostrava as duas, com a contagem partida ao meio — 24 aqui, 12 ali, e a
   * mesma marca duas vezes na lista. O filtro sempre as juntou (compara
   * normalizado); era só a lista a mentir. Mostra-se a grafia mais usada.
   */
  function contarMarcas(lista) {
    var conta = Object.create(null);
    for (var i = 0; i < lista.length; i++) {
      var v = lista[i].doc.v || '';
      var chave = normalizar(v);
      var e = conta[chave];
      if (!e) { conta[chave] = { chave: chave, grafias: Object.create(null), n: 0 }; e = conta[chave]; }
      e.grafias[v] = (e.grafias[v] || 0) + 1;
      e.n++;
    }
    return Object.keys(conta).map(function (k) {
      var e = conta[k];
      e.valor = Object.keys(e.grafias).sort(function (a, b) {
        return e.grafias[b] - e.grafias[a];
      })[0];
      return e;
    }).sort(function (a, b) { return b.n - a.n; });
  }

  /**
   * Uma caixa de filtro é um <button>, não um <label> com <input>.
   *
   * Com o <label>, clicar no texto gera dois cliques — o do rato e o que o
   * browser reencaminha para o <input> — e a faceta ligava e desligava no mesmo
   * gesto. Um botão com `aria-pressed` não tem esse problema, continua a
   * funcionar com o teclado e é lido como o que é.
   */
  function caixa(atributos, rotulo, n, ligado) {
    return '<button type="button" class="msc__caixa' + (ligado ? ' is-on' : '') + '"' +
      ' aria-pressed="' + (ligado ? 'true' : 'false') + '"' + atributos + '>' +
      '<span class="msc__marca-caixa" aria-hidden="true"></span>' +
      '<span class="msc__caixa-txt">' + escaparHtml(rotulo) + '</span>' +
      '<span class="msc__n">' + n + '</span></button>';
  }

  function atr(nome, valor) {
    return valor === null || valor === undefined ? '' : ' ' + nome + '="' + escaparHtml(valor) + '"';
  }

  /* ------------------------------------------------------------- a camada */

  /* `pagina` (17/10/2026): a mesma camada, mas pousada na página de uma coleção
     em vez de aberta por cima da loja. Ver `montarColecao`. */
  function Camada(cfg, motor, pagina) {
    this.cfg = cfg;
    this.motor = motor;
    this.pagina = pagina || null;
    this.base = [];
    this.total = 0;
    this.timerRegisto = null;
    this.ultimaRegistada = null;
    this.aberta = false;
    this.semQuery = true;
    this.aviso = null;
    this.repor();
    this.construir();
  }

  Camada.prototype.repor = function () {
    this.estado = {
      q: '',
      ordem: 'relevancia',
      atributos: Object.create(null),   // chave -> { valor: true }
      marcas: Object.create(null),      // nome normalizado -> true
      stock: false,
      pmin: null,
      pmax: null,
      mostrados: PAGINA,
      abertas: Object.create(null),     // grupos com o «ver mais» aberto
    };
  };

  Camada.prototype.construir = function () {
    if (this.pagina) { this.construirPagina(); return; }
    var self = this;
    var raiz = document.createElement('div');
    raiz.className = 'msc';
    raiz.hidden = true;
    raiz.setAttribute('role', 'dialog');
    raiz.setAttribute('aria-modal', 'true');
    raiz.setAttribute('aria-label', 'Pesquisa');
    raiz.innerHTML =
      '<div class="msc__topo"><div class="msc__topo-int">' +
        '<form class="msc__forma" action="/search" method="get" role="search">' +
          '<span class="msc__lupa" aria-hidden="true"></span>' +
          '<input class="msc__campo" type="search" name="q" autocomplete="off"' +
          ' autocapitalize="off" spellcheck="false" placeholder="O que procura?">' +
          '<button class="msc__limpar" type="button" hidden title="Limpar o que escrevi"' +
          ' aria-label="Limpar o que escrevi">&times;</button>' +
          // O botao de fechar diz «Fechar». Dois «x» iguais lado a lado, um para
          // limpar o campo e outro para sair, e ninguem sabe qual e qual.
          '<button class="msc__fechar" type="button">Fechar</button>' +
        '</form>' +
        '<div class="msc__atalhos"></div>' +
      '</div></div>' +
      '<div class="msc__corpo">' +
        '<aside class="msc__filtros" aria-label="Filtros"></aside>' +
        '<div class="msc__painel">' +
          '<div class="msc__barra"></div>' +
          '<div class="msc__grelha"></div>' +
          '<div class="msc__fim"></div>' +
        '</div>' +
      '</div>' +
      /* A barra do carrinho. Nasce escondida e so aparece depois de alguem
         meter alguma coisa la dentro -- antes disso nao ha nada para dizer e
         so roubava uma faixa do ecra.

         Fica FORA do corpo de proposito: o corpo rola, ela nao. */
      '<div class="msc__carrinho" hidden>' +
        '<span class="msc__carrinho-conta"></span>' +
        '<a class="msc__carrinho-ir" href="/cart">Finalizar</a>' +
      '</div>';
    document.body.appendChild(raiz);

    this.raiz = raiz;
    this.campo = raiz.querySelector('.msc__campo');
    this.atalhos = raiz.querySelector('.msc__atalhos');
    this.filtros = raiz.querySelector('.msc__filtros');
    this.barra = raiz.querySelector('.msc__barra');
    this.grelha = raiz.querySelector('.msc__grelha');
    this.carrinho = raiz.querySelector('.msc__carrinho');
    this.ultimoCliqueFora = 0;
    this.fim = raiz.querySelector('.msc__fim');
    this.botaoLimpar = raiz.querySelector('.msc__limpar');

    var timer = null;
    this.campo.addEventListener('input', function () {
      clearTimeout(timer);
      timer = setTimeout(function () { self.procurar(self.campo.value); }, DEBOUNCE_MS);
    });

    // Enter não recarrega a página: os resultados já estão à frente de quem
    // escreveu, e recarregar era trocá-los por uma versão pior.
    raiz.querySelector('.msc__forma').addEventListener('submit', function (e) {
      e.preventDefault();
      clearTimeout(timer);
      self.procurar(self.campo.value);
      self.campo.blur();
    });

    this.botaoLimpar.addEventListener('click', function () {
      self.campo.value = '';
      if (self.campoDoTema) self.campoDoTema.value = '';
      self.procurar('');
      (self.doTema && self.campoDoTema ? self.campoDoTema : self.campo).focus();
    });

    raiz.querySelector('.msc__fechar').addEventListener('click', function () { self.fechar(); });

    /* Carregar na loja fecha a camada.
     *
     * A camada cobre a pagina mas deixa o cabecalho de fora, de proposito --
     * e para a loja continuar a ser a loja enquanto se procura. So que isso
     * quer dizer que o carrinho, o menu e o logotipo continuam a responder ao
     * dedo, e a gaveta que o tema abre fica ATRAS de nos: z-index 161 contra
     * 10000. A pessoa carrega no carrinho e nao ve carrinho nenhum.
     *
     * Podia disputar o z-index com o tema. Nao vale a pena: quem carrega no
     * cabecalho da loja quer a loja, nao quer a pesquisa. Saimos da frente.
     *
     * Na fase de captura para chegar antes de o tema abrir o que quer que va
     * abrir -- assim a gaveta dele ja se abre com o caminho livre.
     *
     * A caixa de pesquisa do tema e a excepcao obvia: e ela que nos abre, e o
     * `focus` dela chega antes deste clique. Sem esta guarda, a camada fechava
     * no mesmo toque que a mandou abrir. */
    document.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('.msc')) return;
      if (self.campoDoTema &&
          (e.target === self.campoDoTema || self.campoDoTema.contains(e.target))) return;

      /* Guardado mesmo com a camada fechada: serve para a gaveta do tema saber
         que houve um dedo de permeio. Ver o `fecharGavetaDoTema`. */
      self.ultimoCliqueFora = Date.now();
      if (!self.aberta) return;

      /* `true` = fechar SEM voltar atras no historico.
       *
       * O `fechar()` normal faz `history.back()`, para desfazer a entrada que
       * o abrir criou. Sai bem no botao «Fechar» e no Escape. Aqui nao: a
       * pessoa carregou no carrinho da loja, a camada fechava-se, o browser
       * navegava para tras, e a gaveta nunca chegava a abrir. Parecia um
       * carrinho partido e era esta linha.
       *
       * Fica uma entrada de historico por gastar, e o preco disso e um toque
       * no botao «voltar» que nao faz nada visivel. Troca barata. */
      self.fechar(true);
    }, true);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.aberta) self.fechar();
    });

    this.ligarCliques();
  };

  /**
   * A camada como grelha da página de coleção.
   *
   * São as mesmas peças -- atalhos, facetas, barra, grelha, carrinho -- e por
   * isso o mesmo aspecto e o mesmo motor. O que sai é tudo o que é de camada:
   * a caixa de pesquisa (o cabeçalho da loja já tem uma), o «Fechar», o
   * histórico e o clique fora. Os atalhos descem para cima da grelha, onde
   * ficam alinhados com ela em vez de atravessarem o ecrã.
   *
   * Nasce escondida: só se mostra, e só esconde a grelha do tema, depois de ter
   * alguma coisa desenhada (ver `mostrarColecao`).
   */
  Camada.prototype.construirPagina = function () {
    var raiz = document.createElement('div');
    raiz.className = 'msc msc--pagina';
    raiz.hidden = true;
    raiz.setAttribute('aria-label', 'Produtos da coleção');
    raiz.innerHTML =
      '<div class="msc__corpo">' +
        '<aside class="msc__filtros" aria-label="Filtros"></aside>' +
        '<div class="msc__painel">' +
          '<div class="msc__atalhos"></div>' +
          '<div class="msc__barra"></div>' +
          '<div class="msc__grelha"></div>' +
          '<div class="msc__fim"></div>' +
        '</div>' +
      '</div>' +
      '<div class="msc__carrinho" hidden>' +
        '<span class="msc__carrinho-conta"></span>' +
        '<a class="msc__carrinho-ir" href="/cart">Finalizar</a>' +
      '</div>';
    this.pagina.embrulho.parentNode.insertBefore(raiz, this.pagina.embrulho);

    this.raiz = raiz;
    this.campo = null;
    this.botaoLimpar = null;
    this.atalhos = raiz.querySelector('.msc__atalhos');
    this.filtros = raiz.querySelector('.msc__filtros');
    this.barra = raiz.querySelector('.msc__barra');
    this.grelha = raiz.querySelector('.msc__grelha');
    this.carrinho = raiz.querySelector('.msc__carrinho');
    this.fim = raiz.querySelector('.msc__fim');
    this.ultimoCliqueFora = 0;
    // «Aberta» para sempre: é o que deixa o `confirmarStock` redesenhar.
    this.aberta = true;
    this.ligarCliques();
  };

  /**
   * Põe a coleção na página e tira de vista a grelha do tema.
   *
   * A grelha do tema não sai do HTML: fica para o Google, que lê a página como
   * a Shopify a serve, e para o caso de alguma coisa aqui falhar. Só se esconde.
   * O título (o <h1>) e a descrição da coleção passam para fora dela, para
   * continuarem à vista.
   */
  Camada.prototype.mostrarColecao = function (base) {
    var pg = this.pagina;
    this.base = base;
    this.total = base.length;
    this.semQuery = false;
    this.aviso = null;
    if (pg.ordem) this.estado.ordem = pg.ordem;
    /* «Em stock» vem ligado, como fazia o bukprod-custom.js na grelha do tema
       (com um recarregamento da página). Desliga-se na coluna dos filtros. */
    this.estado.stock = true;
    var voltou = this.lerEstado();
    this.desenhar();

    var antes = pg.embrulho;
    var cabeca = antes.querySelector('header');
    if (cabeca) antes.parentNode.insertBefore(cabeca, this.raiz);
    var depois = this.raiz.nextSibling;
    Array.prototype.forEach.call(antes.querySelectorAll(':scope > div > article'), function (a) {
      if (a.textContent.trim()) antes.parentNode.insertBefore(a, depois);
    });
    antes.classList.add('msc-substituida');
    antes.setAttribute('aria-hidden', 'true');
    this.raiz.hidden = false;
    this.lerCarrinho();

    var self = this;
    window.addEventListener('pagehide', function () { self.guardarEstado(true); });
    if (voltou && voltou.y) window.scrollTo(0, voltou.y);
  };

  /* Os filtros e o «mostrar mais» sobrevivem a ir a um produto e voltar.
   *
   * Sem isto, o botão «voltar» trazia a coleção do princípio: filtros a zero,
   * só os primeiros 48, e a pessoa no topo da página. Só se repõe quando se
   * volta (ou se recarrega) -- quem entra na coleção por um link entra nela
   * limpa, como esperaria. */
  Camada.prototype.chaveEstado = function () {
    return 'msc-col:' + this.pagina.handle;
  };

  Camada.prototype.guardarEstado = function (comPosicao) {
    var e = this.estado;
    try {
      var antigo = comPosicao ? null : JSON.parse(sessionStorage.getItem(this.chaveEstado()) || 'null');
      sessionStorage.setItem(this.chaveEstado(), JSON.stringify({
        o: e.ordem, a: e.atributos, m: e.marcas, s: e.stock, pmin: e.pmin, pmax: e.pmax,
        n: e.mostrados, y: comPosicao ? window.scrollY : (antigo && antigo.y) || 0, t: Date.now(),
      }));
    } catch (erro) { /* modo privado, quota: fica sem memória, e mais nada */ }
  };

  Camada.prototype.lerEstado = function () {
    var nav = (performance.getEntriesByType && performance.getEntriesByType('navigation')[0]) || {};
    if (nav.type !== 'back_forward' && nav.type !== 'reload') return null;
    try {
      var g = JSON.parse(sessionStorage.getItem(this.chaveEstado()) || 'null');
      if (!g || Date.now() - g.t > 30 * 60 * 1000) return null;
      var e = this.estado;
      var atributos = Object.create(null);
      Object.keys(g.a || {}).forEach(function (k) {
        atributos[k] = Object.create(null);
        Object.keys(g.a[k]).forEach(function (v) { atributos[k][v] = true; });
      });
      e.atributos = atributos;
      e.marcas = Object.create(null);
      Object.keys(g.m || {}).forEach(function (m) { e.marcas[m] = true; });
      e.ordem = g.o || e.ordem;
      e.stock = !!g.s;
      e.pmin = typeof g.pmin === 'number' ? g.pmin : null;
      e.pmax = typeof g.pmax === 'number' ? g.pmax : null;
      e.mostrados = Math.max(PAGINA, g.n || 0);
      return g;
    } catch (erro) { return null; }
  };

  /**
   * Um só ouvinte por zona, em vez de um por caixa.
   *
   * A lista de facetas é reescrita a cada clique — são dezenas de elementos a
   * nascer e a morrer o tempo todo. Pendurar ouvintes em cada um era pagar isso
   * outra vez a cada recontagem, e esquecer-se de os tirar.
   */
  Camada.prototype.ligarCliques = function () {
    var self = this;

    /* A moldura fica cinzenta enquanto a fotografia não chega, e passa a branca
       quando chega. Branca desde o início, uma foto ainda a caminho parecia uma
       foto que não existe (César, 17/10/2026). `load` não sobe na árvore: só se
       apanha na fase de captura. Uma foto que falha fica no cinzento. */
    this.grelha.addEventListener('load', function (e) {
      var img = e.target;
      if (img && img.classList && img.classList.contains('msc__img') && img.parentNode) {
        img.parentNode.classList.add('is-pronta');
      }
    }, true);

    function alternar(chave, valor) {
      var grupo = self.estado.atributos[chave] || (self.estado.atributos[chave] = Object.create(null));
      if (grupo[valor]) delete grupo[valor]; else grupo[valor] = true;
    }

    this.filtros.addEventListener('click', function (e) {
      var alvo = e.target.closest('[data-msc]');
      if (!alvo || !self.filtros.contains(alvo)) return;
      var acao = alvo.getAttribute('data-msc');

      if (acao === 'atributo') {
        alternar(alvo.getAttribute('data-chave'), alvo.getAttribute('data-valor'));
      } else if (acao === 'marca') {
        var m = alvo.getAttribute('data-valor');
        if (self.estado.marcas[m]) delete self.estado.marcas[m];
        else self.estado.marcas[m] = true;
      } else if (acao === 'stock') {
        self.estado.stock = !self.estado.stock;
      } else if (acao === 'mais') {
        self.estado.abertas[alvo.getAttribute('data-chave')] = true;
      } else if (acao === 'fechar-filtros') {
        self.raiz.classList.remove('msc--filtros-abertos');
        return;
      } else if (acao === 'limpar') {
        var q = self.estado.q, ordem = self.estado.ordem;
        self.repor();
        self.estado.q = q;
        self.estado.ordem = ordem;
      } else {
        return;
      }
      if (acao !== 'mais') self.estado.mostrados = PAGINA;
      self.desenhar();
    });

    // O preço escreve-se, não se clica.
    this.filtros.addEventListener('change', function (e) {
      var alvo = e.target;
      if (!alvo.getAttribute || alvo.getAttribute('data-msc') !== 'preco') return;
      var n = parseFloat(String(alvo.value).replace(',', '.'));
      self.estado[alvo.getAttribute('data-lado')] = isNaN(n) ? null : n;
      self.estado.mostrados = PAGINA;
      self.desenhar();
    });

    this.atalhos.addEventListener('click', function (e) {
      var alvo = e.target.closest('[data-msc="atalho"]');
      if (!alvo) return;
      alternar('tipo', alvo.getAttribute('data-valor'));
      self.estado.mostrados = PAGINA;
      self.desenhar();
    });

    this.barra.addEventListener('change', function (e) {
      if (!e.target.getAttribute || e.target.getAttribute('data-msc') !== 'ordem') return;
      self.estado.ordem = e.target.value;
      self.estado.mostrados = PAGINA;
      self.desenhar();
    });

    this.barra.addEventListener('click', function (e) {
      if (e.target.closest('[data-msc="abrir-filtros"]')) {
        self.raiz.classList.toggle('msc--filtros-abertos');
      }
    });

    this.fim.addEventListener('click', function (e) {
      if (!e.target.closest('[data-msc="mais-produtos"]')) return;
      self.estado.mostrados += PAGINA;
      self.desenhar();
    });

    // Meter no carrinho sem sair da pesquisa. Vem antes do registo porque o
    // botao esta FORA do <a> e o `closest('.msc__cartao')` nao o apanha.
    this.grelha.addEventListener('click', function (e) {
      var botao = e.target.closest('[data-msc="comprar"]');
      if (!botao) return;
      e.preventDefault();
      self.meterNoCarrinho(botao);
    });

    // O registo do que foi procurado segue no clique, com o produto escolhido.
    this.grelha.addEventListener('click', function (e) {
      /* `[data-handle]` e nao `.msc__cartao`: as duas ligacoes do cartao levam
         o handle, e carregar na imagem conta tanto como carregar no nome. */
      var cartao = e.target.closest('[data-handle]');
      if (!cartao) return;
      clearTimeout(self.timerRegisto);
      self.ultimaRegistada = self.estado.q;
      self.registar(self.estado.q, self.total, cartao.getAttribute('data-handle'));
    });
  };

  /* ---------------------------------------------------------------- abrir */

  Camada.prototype.abrir = function (queryInicial) {
    if (this.aberta) return;
    this.aberta = true;
    this.raiz.hidden = false;
    var topo = fundoDoCabecalho(this.cfg.cabecalho);
    /* No computador a camada começa logo por baixo da linha da caixa de
       pesquisa, por cima do menu das categorias. Enquanto se procura o menu só
       ocupava uma faixa entre a caixa e os resultados (César, 17/10/2026). */
    var linha = linhaDaCaixa(this.campoDoTema);
    if (linha && linha < topo) topo = linha;
    this.raiz.style.top = topo + 'px';
    document.documentElement.classList.add('msc-preso');

    /* Duas caixas de pesquisa na mesma página é uma a mais.
     *
     * Com a camada por baixo do cabeçalho, a caixa do tema fica à vista por
     * cima dela — e é essa que a pessoa acabou de usar. Então é essa que manda,
     * e a nossa esconde-se. Quando ela não está à vista (no telemóvel o tema
     * fecha-a atrás de uma lupa), a nossa aparece e faz o trabalho. */
    this.doTema = mandaACaixaDoTema(this.campoDoTema, topo);
    this.raiz.classList.toggle('msc--sem-campo', this.doTema);

    // O botão «voltar» do telemóvel tem de fechar a camada, não sair da loja.
    try {
      history.pushState({ msc: true }, '');
      this.comHistoria = true;
    } catch (e) { this.comHistoria = false; }

    var campo = this.doTema ? this.campoDoTema : this.campo;
    campo.value = queryInicial || '';
    this.campo.value = queryInicial || '';
    this.lerCarrinho();
    this.procurar(queryInicial || '');
    setTimeout(function () { campo.focus(); }, 30);
  };

  /** `semHistoria` = já viemos de um popstate; mexer no histórico outra vez
      mandava a pessoa para a página anterior à loja. */
  Camada.prototype.fechar = function (semHistoria) {
    if (!this.aberta) return;
    this.aberta = false;
    this.raiz.hidden = true;
    if (this.doTema && this.campoDoTema) this.campoDoTema.blur();
    this.raiz.classList.remove('msc--filtros-abertos');
    document.documentElement.classList.remove('msc-preso');
    var voltar = this.comHistoria && !semHistoria;
    this.comHistoria = false;
    if (voltar) { try { history.back(); } catch (e) {} }
  };

  /* -------------------------------------------------------------- procurar */

  Camada.prototype.procurar = function (query) {
    var q = (query || '').trim();

    /* Uma pergunta nova apaga os filtros da anterior.
     *
     * Sem isto, escrever «cabo» depois de ter marcado «Marca: Karma» devolvia
     * zero — e a explicação estava numa coluna que no telemóvel nem sequer se
     * vê. Filtros são sobre uma lista; trocar a lista deixa-os sem assunto. */
    if (q !== this.estado.q) {
      var ordem = this.estado.ordem;
      this.repor();
      this.estado.ordem = ordem;
    }

    this.estado.q = q;
    this.estado.mostrados = PAGINA;
    this.botaoLimpar.hidden = !q;

    if (q.length < 2) {
      this.base = this.destaques();
      this.total = this.base.length;
      this.semQuery = true;
      this.aviso = null;
    } else {
      var r = this.motor.pesquisar(q, { limite: MAX_BASE });
      /* Numa pesquisa o sem stock aparece, atrás de tudo o que há e do
         separador (ver `ordenar`): quem o procura tem de o poder encontrar.
         Nos tops (`destaques`) é que não ocupa lugar. — César, 14/10/2026 */
      this.base = r.resultados;
      this.total = r.total;
      this.aviso = r;
      this.semQuery = false;
      this.agendarRegisto(q, r.total);
    }
    this.desenhar();
  };

  /**
   * O que mostrar antes de escreverem o que quer que seja.
   *
   * São os mais vendidos: `u` é um escalão de 1 a 5 que o indexador já traz. Uma
   * camada que abre vazia parece avariada, e o catálogo tem mais de oito mil
   * produtos — mostrar os primeiros por ordem de ficheiro não dizia nada a
   * ninguém.
   */
  Camada.prototype.destaques = function () {
    /* Dois tops, intercalados: venda, aluguer, venda, aluguer… (14/10/2026,
       César) — juntos, a montra abria com uma fila só de alugueres ou só de
       vendas. Acabando os alugueres, seguem as vendas. Só com stock. */
    var vendas = [], alugueres = [];
    var docs = this.motor.docs;
    for (var i = 0; i < docs.length; i++) {
      if (!docs[i].d) continue;
      var escalao = docs[i].uv !== undefined ? docs[i].uv : (docs[i].u || 0) * 2;
      var item = { doc: docs[i], score: escalao + (docs[i].nv ? 1 : 0) };
      (docs[i].h.indexOf('aluguer') !== -1 ? alugueres : vendas).push(item);
    }
    var maior = function (a, b) { return b.score - a.score; };
    vendas.sort(maior); alugueres.sort(maior);
    var lista = [];
    for (var k = 0; k < Math.max(vendas.length, alugueres.length); k++) {
      if (k < vendas.length) lista.push(vendas[k]);
      if (k < alugueres.length) lista.push(alugueres[k]);
    }
    // A ordem fica no score, para a ordenação «relevância» não a desfazer.
    lista.forEach(function (x, n) { x.score = lista.length - n; });
    return lista;
  };

  /**
   * Ha algum filtro escolhido?
   *
   * Serve para distinguir <<ainda nao disse o que quero>> de <<escolhi uma
   * categoria>>. No primeiro caso a montra abre nos mais vendidos; no segundo,
   * a pessoa ja disse o que quer, e quer tudo o que ha disso.
   */
  Camada.prototype.semFiltros = function () {
    var e = this.estado;
    if (e.stock || e.pmin !== null || e.pmax !== null) return false;
    if (Object.keys(e.marcas).length) return false;
    var chaves = Object.keys(e.atributos);
    for (var i = 0; i < chaves.length; i++) {
      if (Object.keys(e.atributos[chaves[i]]).length) return false;
    }
    return true;
  };

  /* -------------------------------------------------------------- filtrar */

  /**
   * Os resultados que passam nos filtros — todos menos um, se `excepto` vier.
   *
   * A excepção é o que faz as contagens servirem para alguma coisa. Ao contar as
   * marcas, a marca já escolhida não conta como filtro: senão, escolher «Fos»
   * punha todas as outras marcas a zero e a pessoa ficava sem saber para onde ir
   * a seguir, a não ser para trás.
   */
  Camada.prototype.filtrar = function (excepto) {
    var e = this.estado;
    var marcas = Object.keys(e.marcas);
    var chaves = Object.keys(e.atributos).filter(function (k) {
      return Object.keys(e.atributos[k]).length;
    });

    return this.base.filter(function (r) {
      var d = r.doc;
      if (e.stock && excepto !== 'stock' && !d.d) return false;
      if (excepto !== 'preco') {
        if (e.pmin !== null && !(d.p >= e.pmin)) return false;
        if (e.pmax !== null && !(d.p <= e.pmax)) return false;
      }
      if (marcas.length && excepto !== 'marca' && marcas.indexOf(normalizar(d.v)) === -1) return false;

      for (var i = 0; i < chaves.length; i++) {
        var k = chaves[i];
        if (excepto === k) continue;
        var querem = e.atributos[k];
        var tem = valoresDe(d, k);
        var bate = false;
        for (var j = 0; j < tem.length; j++) {
          if (querem[String(tem[j])]) { bate = true; break; }
        }
        if (!bate) return false;
      }
      return true;
    });
  };

  /**
   * A ordem dos resultados. **O que há em stock vem sempre primeiro.**
   *
   * É regra da loja, não uma afinação de relevância: primeiro tudo o que se
   * pode vender hoje, e só depois o que é sob consulta. Vale para as quatro
   * ordenações — escolher «preço: mais barato» ordena os que há, e os que não
   * há continuam atrás, ordenados entre si.
   *
   * O que isto custa, dito por inteiro: procurar um modelo concreto que está
   * esgotado empurra-o para baixo de tudo o que houver em stock. Fica lá, e o
   * separador na grelha diz onde começa essa parte — mas quem procura uma
   * referência exacta vai ter de descer. É o preço de a montra mostrar
   * primeiro o que se vende.
   */
  Camada.prototype.ordenar = function (lista) {
    var ordem = this.estado.ordem;
    var entre;
    if (ordem === 'preco-asc') entre = function (a, b) { return (a.doc.p || 0) - (b.doc.p || 0); };
    else if (ordem === 'preco-desc') entre = function (a, b) { return (b.doc.p || 0) - (a.doc.p || 0); };
    else if (ordem === 'nome') entre = function (a, b) { return a.doc.t.localeCompare(b.doc.t, 'pt'); };
    else entre = function (a, b) { return (b.score || 0) - (a.score || 0); };

    return lista.slice().sort(function (a, b) {
      var da = a.doc.d ? 1 : 0, db = b.doc.d ? 1 : 0;
      if (da !== db) return db - da;
      return entre(a, b);
    });
  };

  /* ------------------------------------------------------------- desenhar */

  Camada.prototype.desenhar = function () {
    var visiveis = this.ordenar(this.filtrar(null));
    this.desenharAtalhos();
    this.desenharFiltros(visiveis.length);
    this.desenharBarra(visiveis.length);
    this.desenharGrelha(visiveis);
    this.confirmarStock(visiveis.slice(0, this.estado.mostrados));
    if (this.pagina) this.guardarEstado(false);
  };

  /**
   * Pergunta à loja, ao vivo, se o que está na grelha tem stock.
   *
   * O índice é uma fotografia do dia em que se correu o `correr.cmd`. O
   * alicate de pontas M76-G.YP3 entrou em stock depois da de 18/09 e a grelha
   * continuou a dizer «Sem stock» enquanto a ficha dizia «8 em stock». O
   * stock muda todos os dias; o índice não.
   *
   * Só os cartões à vista, seis pedidos de cada vez, e cada um outra vez se a
   * resposta tiver mais de um minuto. Havendo diferenças, redesenha uma vez no
   * fim, e o artigo sobe para o lado de cima do separador. Falhando o pedido,
   * fica o que o índice diz.
   *
   * Pede-se `/products/<handle>?view=ms-vivo` (templates/product.ms-vivo.liquid):
   * o `.js` não diz quantas unidades há, e o César quer ver o −1 de uma venda e
   * o +x de uma entrada sem correr o indexador. Preço e unidades vêm sempre
   * daqui, e não só quando o artigo muda de «sem stock» para «em stock». Num
   * tema sem esse ficheiro a Shopify devolve a ficha normal em HTML; aí cai-se
   * para o `.js`, que ao menos confirma se há stock.
   */
  Camada.prototype.confirmarStock = function (lista) {
    var self = this;
    var agora = Date.now();
    var fila = lista.map(function (r) { return r.doc; })
      .filter(function (d) { return !d.aConfirmar && !(d.vivoEm && agora - d.vivoEm < 60000); });
    if (!fila.length || typeof fetch !== 'function') return;
    fila.forEach(function (d) { d.aConfirmar = true; });

    var mudou = false;
    var aCorrer = 0;

    function acabou() {
      aCorrer--;
      if (fila.length) { seguinte(); return; }
      if (aCorrer === 0 && mudou && self.aberta !== false) self.desenhar();
    }

    /* Variantes no mesmo formato, venham do ms-vivo ou do .js:
     * {id, p, pc, a, q} com preços em cêntimos e q=null quando não se sabe. */
    function doVivo(j) { return j && j.v; }
    function doJs(p) {
      return (p && p.variants || []).map(function (v) {
        return { id: v.id, p: v.price, pc: v.compare_at_price, a: v.available, q: null };
      });
    }

    function aplicar(d, vs) {
      /* A variante do cartão é a mais barata das que se podem comprar — a mesma
       * regra do índice, para o preço do cartão ser o do carrinho. Sem nenhuma
       * disponível, mostra-se o preço da mais barata e o cartão diz «Sem stock». */
      var porPreco = vs.slice().sort(function (a, b) { return a.p - b.p; });
      // «Vender sem stock» deixa a variante disponível com 0 unidades: a ficha diz
      // «Sem stock», e o cartão também (14/10, painel BNC GTL-0033).
      var livres = porPreco.filter(function (v) { return v.a && !(v.q != null && v.q <= 0); });
      var v = livres[0] || porPreco[0];
      if (!v) return;
      var tem = livres.length > 0;
      var q = tem && v.q != null ? Math.max(0, v.q) : 0;
      var p = v.p / 100;
      var pc = v.pc && v.pc > v.p ? v.pc / 100 : null;
      var vid = tem ? v.id : null;
      // Sem número (o .js, ou uma variante que não conta stock) não se apaga o
      // que o índice sabia se o estado não mudou — mas uma venda nunca fica
      // escondida atrás do número velho, porque o ms-vivo traz sempre o novo.
      if (v.q == null && tem && d.d) q = d.q || 0;
      if (tem !== !!d.d || q !== (d.q || 0) || p !== d.p || pc !== (d.pc || null) || vid !== (d.vid || null)) {
        mudou = true;
      }
      d.d = tem; d.q = q; d.p = p; d.pc = pc; d.vid = vid;
    }

    function pedirJs(d) {
      return fetch('/products/' + encodeURIComponent(d.h) + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(doJs);
    }

    function seguinte() {
      var d = fila.shift();
      if (!d) return;
      aCorrer++;
      // O `_` muda a cada pedido: a Shopify guarda as páginas na cache dela pelo
      // endereço, e sem isto podia devolver a resposta de há uns minutos.
      fetch('/products/' + encodeURIComponent(d.h) + '?view=ms-vivo&_=' + Date.now(), { cache: 'no-store' })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (t) {
          var vs = null;
          try { vs = doVivo(JSON.parse(t)); } catch (e) { vs = null; }
          return vs || pedirJs(d);
        })
        .then(function (vs) {
          d.aConfirmar = false;
          if (vs && vs.length) { d.vivoEm = Date.now(); aplicar(d, vs); }
        })
        .catch(function () { d.aConfirmar = false; })
        .then(acabou);
    }

    for (var i = 0; i < 6; i++) seguinte();
  };

  Camada.prototype.desenharAtalhos = function () {
    // As categorias do que está à frente, em botões grandes. No telemóvel é por
    // aqui que se filtra: a coluna das facetas está fechada numa gaveta.
    var self = this;
    var tipos = contar(this.filtrar('tipo'), function (d) { return valoresDe(d, 'tipo'); })
      .filter(function (x) { return x.valor !== 'outro'; });

    /* A lista e a ordem vêm do painel, quando lá estiverem escolhidas.
     *
     * Os que não aparecem nos resultados ficam de fora à mesma, mesmo estando
     * configurados: um botão «Microfone 0» não leva a lado nenhum e só ocupa a
     * linha. A configuração escolhe quais PODEM aparecer e por que ordem; os
     * resultados é que decidem quais fazem sentido agora. */
    var escolhidos = (this.cfg.atalhos && this.cfg.atalhos.length) ? this.cfg.atalhos : null;
    if (escolhidos) {
      var porValor = Object.create(null);
      tipos.forEach(function (t) { porValor[t.valor] = t; });
      tipos = escolhidos.map(function (v) { return porValor[v]; }).filter(Boolean);
    } else {
      // Doze e nao oito: com oito sobrava meia linha em branco ate ao botao
      // de fechar, e cada circulo que la cabe e uma categoria que a pessoa
      // deixa de ter de escrever. Os que nao couberem passam para baixo.
      tipos = tipos.slice(0, 12);
    }
    if (tipos.length < 2) { this.atalhos.innerHTML = ''; return; }

    var escolhidos = this.estado.atributos.tipo || {};
    this.atalhos.innerHTML = tipos.map(function (t) {
      return '<button type="button" class="msc__atalho' + (escolhidos[t.valor] ? ' is-on' : '') + '"' +
        ' aria-pressed="' + (escolhidos[t.valor] ? 'true' : 'false') + '"' +
        ' data-msc="atalho"' + atr('data-valor', t.valor) + '>' +
        escaparHtml(self.motor.rotuloTipo(t.valor)) +
        '<span class="msc__atalho-n">' + t.n + '</span></button>';
    }).join('');
  };

  Camada.prototype.desenharFiltros = function (nVisiveis) {
    var self = this;
    var e = this.estado;
    var html = '';

    var escolhas = Object.keys(e.marcas).length +
      Object.keys(e.atributos).reduce(function (s, k) { return s + Object.keys(e.atributos[k]).length; }, 0) +
      (e.stock ? 1 : 0) + (e.pmin !== null ? 1 : 0) + (e.pmax !== null ? 1 : 0);

    html += '<div class="msc__filtros-topo"><span>Filtros</span>' +
      (escolhas ? '<button type="button" class="msc__limpar-filtros" data-msc="limpar">' +
        'Limpar (' + escolhas + ')</button>' : '') + '</div>';

    // Só se vê no telemóvel, onde as facetas estão numa gaveta por cima da
    // grelha: sem esta porta, escolher um filtro deixava a pessoa a olhar para
    // os filtros em vez de para o que eles deram.
    html += '<button type="button" class="msc__fechar-filtros" data-msc="fechar-filtros">' +
      'Ver ' + nVisiveis + (nVisiveis === 1 ? ' resultado' : ' resultados') + '</button>';

    var comStock = this.filtrar('stock').filter(function (r) { return r.doc.d; }).length;
    html += '<div class="msc__grupo msc__grupo--solto">' +
      caixa(' data-msc="stock"', 'Em stock', comStock, e.stock) + '</div>';

    html += '<div class="msc__grupo"><h3>Preço</h3><div class="msc__preco">' +
      '<input type="number" inputmode="decimal" min="0" step="1" data-msc="preco" data-lado="pmin"' +
      ' aria-label="Preço mínimo" placeholder="mín"' + atr('value', e.pmin) + '>' +
      '<span>até</span>' +
      '<input type="number" inputmode="decimal" min="0" step="1" data-msc="preco" data-lado="pmax"' +
      ' aria-label="Preço máximo" placeholder="máx"' + atr('value', e.pmax) + '>' +
      '</div></div>';

    var marcas = contarMarcas(this.filtrar('marca'));
    if (marcas.length > 1) {
      html += '<div class="msc__grupo"><h3>Marca</h3>' +
        grupo('__marca', marcas,
              function (m) { return m.valor; },
              function (m) { return !!e.marcas[m.chave]; },
              function (m) { return ' data-msc="marca"' + atr('data-valor', m.chave); }) +
        '</div>';
    }

    FACETAS.forEach(function (f) {
      var vals = contar(self.filtrar(f.chave), function (d) { return valoresDe(d, f.chave); });
      if (!vals.length) return;

      var ligado = function (v) {
        return !!(e.atributos[f.chave] && e.atributos[f.chave][v.valor]);
      };
      var atributos = function (v) {
        return ' data-msc="atributo"' + atr('data-chave', f.chave) + atr('data-valor', v.valor);
      };

      if (f.bandeira) {
        html += '<div class="msc__grupo msc__grupo--solto">' +
          caixa(atributos(vals[0]), f.rotulo, vals[0].n, ligado(vals[0])) + '</div>';
        return;
      }
      if (vals.length < 2) return;

      var rotuloDe = f.chave === 'tipo'
        ? function (v) { return self.motor.rotuloTipo(v.valor); }
        : function (v) { return bonito(v.valor); };

      html += '<div class="msc__grupo"><h3>' + escaparHtml(f.rotulo) + '</h3>' +
        grupo(f.chave, vals, rotuloDe, ligado, atributos) + '</div>';
    });

    this.filtros.innerHTML = html;

    function grupo(chave, vals, rotuloDe, ligadoDe, atributosDe) {
      var aberta = self.estado.abertas[chave];
      var mostrar = aberta ? vals : vals.slice(0, VISIVEIS);
      var out = mostrar.map(function (v) {
        return caixa(atributosDe(v), rotuloDe(v), v.n, ligadoDe(v));
      }).join('');
      if (!aberta && vals.length > VISIVEIS) {
        out += '<button type="button" class="msc__mais-valores" data-msc="mais"' +
          atr('data-chave', chave) + '>ver mais ' + (vals.length - VISIVEIS) + '</button>';
      }
      return out;
    }
  };

  Camada.prototype.desenharBarra = function (n) {
    var ordem = this.estado.ordem;
    var pagina = !!this.pagina;
    var opcoes = ORDENS.map(function (o) {
      // Numa coleção não há pergunta a que ser relevante: é a ordem que a loja
      // lhe deu no admin (manual, mais vendidos...).
      var rotulo = (pagina && o.valor === 'relevancia') ? 'Em destaque' : o.rotulo;
      return '<option value="' + o.valor + '"' + (o.valor === ordem ? ' selected' : '') +
        '>' + rotulo + '</option>';
    }).join('');

    /* <<Os mais vendidos>> so enquanto ninguem escolheu nada. A partir do
       momento em que se clica num circulo, o que esta na grelha sao mesas de
       mistura e nao os mais vendidos -- e a pessoa quer saber quantas sao. */
    var titulo = this.pagina
      ? n + (n === 1 ? ' produto' : ' produtos')
      : (this.semQuery && this.semFiltros())
        ? 'Os mais vendidos'
        : n + (n === 1 ? ' resultado' : ' resultados');

    var nota = '';
    if (!this.semQuery && this.aviso) {
      if (this.aviso.marcaIgnorada) {
        nota = '<p class="msc__nota">Não temos esse artigo da marca <strong>' +
          escaparHtml(this.aviso.marcaIgnorada) + '</strong>. A mostrar de outras marcas.</p>';
      } else if (this.aviso.parcial) {
        nota = '<p class="msc__nota">Poucos artigos com essa característica confirmada — ' +
          'a mostrar também resultados aproximados.</p>';
      }
    }

    this.barra.innerHTML =
      '<div class="msc__barra-linha">' +
        '<button type="button" class="msc__botao-filtros" data-msc="abrir-filtros">Filtros</button>' +
        '<span class="msc__contagem">' + escaparHtml(titulo) + '</span>' +
        '<label class="msc__ordem">Ordenar por ' +
          '<select data-msc="ordem">' + opcoes + '</select>' +
        '</label>' +
      '</div>' + nota;
  };

  /* ------------------------------------------------------------- carrinho */

  /* O contador do carrinho no cabecalho da loja.
   *
   * A loja so o mexe quando e ELA a meter no carrinho. Nos metemos por fora, e
   * o numero ficava a zero enquanto a nossa barra dizia «1 artigo» -- a mesma
   * pagina a dizer duas coisas ao mesmo tempo, que e das coisas que mais
   * depressa faz desconfiar de uma loja.
   *
   * Sao dois elementos, um para cada tamanho de ecra, e o `aria-label` do link
   * leva o total -- esse e para quem ouve a pagina em vez de a ver, e ficava a
   * dizer «Carrinho 0,00 EUR» com artigos la dentro.
   *
   * Nao rebenta se o tema mudar os nomes: nao havendo elemento, nao se faz
   * nada. O pior que acontece e voltarmos ao que ja era. */
  function actualizarContadorDaLoja(carrinho) {
    var n = (carrinho && carrinho.item_count) || 0;
    var total = formatarPreco(((carrinho && carrinho.total_price) || 0) / 100,
      (carrinho && carrinho.currency) || 'EUR');

    var ids = ['cart-count', 'cart-count--m'];
    for (var i = 0; i < ids.length; i++) {
      var el = document.getElementById(ids[i]);
      if (el) el.textContent = String(n);
    }

    var links = document.querySelectorAll('a[data-panel="cart"]');
    for (var j = 0; j < links.length; j++) {
      links[j].setAttribute('aria-label', 'Carrinho ' + total);
    }

    /* A gaveta do tema guarda o estado nestes atributos. Mante-los a par evita
       que ela se abra a dizer um numero e o cabecalho outro. */
    var cab = document.querySelector('#cart header[data-totalqty]');
    if (cab) {
      cab.setAttribute('data-totalqty', String(n));
      cab.setAttribute('data-totalprice', total);
    }
  }

  /* Refazer o conteudo da gaveta do carrinho do tema.
   *
   * O tema desenha essa gaveta NO SERVIDOR, no momento em que a pagina
   * carrega. Nos metemos o artigo depois e por fora, e aquele HTML fica como
   * estava -- a gaveta abria VAZIA numa pagina onde o carrinho ja tinha
   * coisas. Bastava recarregar a pagina para aparecer tudo certo, o que e o
   * pior tipo de erro: parece intermitente e nao e.
   *
   * Pede-se a seccao outra vez com `?sections=side-cart`, que e exactamente o
   * mecanismo que o proprio tema usa para se refazer a si mesmo. Quem desenha
   * continua a ser ele; nos so lhe pedimos que volte a desenhar.
   *
   * Troca-se o INTERIOR e nao o elemento: o `<aside id="cart">` fica o mesmo,
   * com o que quer que o tema lhe tenha pendurado para o abrir e fechar. */
  function refazerGavetaDoTema() {
    var actual = document.getElementById('cart');
    if (!actual) return;

    var raiz = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    pedir('GET', raiz + '?sections=side-cart', null, function (erro, seccoes) {
      if (erro || !seccoes || !seccoes['side-cart']) return;
      try {
        var caixa = document.createElement('div');
        caixa.innerHTML = seccoes['side-cart'];
        var nova = caixa.querySelector('#cart');
        if (nova) actual.innerHTML = nova.innerHTML;
      } catch (e) {
        console.warn('[ms-search] nao consegui refazer a gaveta:', e && e.message);
      }
    });
  }

  /* A loja abre a gaveta dela quando o carrinho muda. Fecha-se.
   *
   * Quem a manda abrir nao somos nos: o `standard-storefront-actions` da
   * Shopify ouve os pedidos ao /cart/add.js e avisa o tema. So que a nossa
   * camada esta por cima -- z-index 10000 contra 161 -- e a gaveta dele fica
   * presa na tira que sobra acima do nosso topo. Duas folhas a disputar o
   * mesmo ecra, e nenhuma das duas utilizavel.
   *
   * Fecha-se pelo botao do PROPRIO tema, e nao tirando-lhe a classe a mao:
   * assim corre o codigo dele, com o que quer que ele faca a seguir, em vez de
   * eu adivinhar o estado interno de um tema que nao escrevi.
   *
   * Espreita-se durante um segundo e meio porque ele abre em diferido, depois
   * do evento. Nao abrindo, nao ha nada a fechar e desiste-se em silencio. */
  function fecharGavetaDoTema(camada) {
    var inicio = Date.now();
    var fim = inicio + 1500;

    (function espreitar() {
      /* Se houve um clique fora da camada depois de termos metido o artigo, a
         gaveta pode ter sido a pessoa a abri-la -- e nesse caso e para ficar.
         Fechar o que o dedo dela acabou de abrir e pior do que deixar as duas
         camadas a disputar o ecra. */
      if (camada && camada.ultimoCliqueFora > inicio) return;

      var gaveta = document.querySelector('.m6pn.toggle');
      if (gaveta) {
        var botao = document.querySelector('.overlay-close');
        if (botao) botao.click();

        /* O tema pode deixar um `overflow` em linha ao fechar, e um estilo em
           linha ganha a classe `msc-preso` com que prendemos a pagina. Sem
           isto, a camada continuava aberta e a loja por baixo passava a rolar
           com o dedo. */
        setTimeout(function () {
          if (document.documentElement.classList.contains('msc-preso')) {
            document.documentElement.style.overflow = '';
            document.body.style.overflow = '';
          }
        }, 50);
        return;
      }
      if (Date.now() < fim) setTimeout(espreitar, 100);
    })();
  }

  /* Meter no carrinho sem sair da pesquisa.
   *
   * Dois pedidos: o `/cart/add.js` mete, o `/cart.js` diz como ficou. Podia
   * contar a soma por mim e poupar o segundo, mas a loja tem regras que eu nao
   * conheco -- limites por artigo, descontos automaticos, brindes -- e uma
   * barra que mente sobre o total e pior do que uma barra que demora 200ms.
   *
   * O botao fica desligado enquanto o pedido corre. Sem isso, dois toques
   * apressados metiam duas unidades sem ninguem querer. */
  /* Falar com a loja por XMLHttpRequest, e nao por fetch.
   *
   * A Shopify embrulha o `window.fetch` para instrumentar o carrinho, e esse
   * embrulho fala com o monorail e com os web-pixels. Num browser com
   * bloqueador -- o Brave traz um de origem, e boa parte dos clientes usa
   * algum -- esses pedidos levam ERR_BLOCKED_BY_CLIENT, a promessa dela
   * rejeita, e a nossa morre com ela. O artigo pode ate ter entrado no
   * carrinho: o que falhou foi a contabilidade, nao a compra.
   *
   * O XHR nao passa por esse embrulho. E a diferenca entre o botao funcionar
   * para toda a gente e funcionar so para quem nao bloqueia anuncios. */
  function pedir(metodo, url, corpo, aoAcabar) {
    /* Tres cuidados, todos por causa de um botao que ficou preso a cinzento.
     *
     * 1. `acabou` -- a resposta chega UMA vez. Sem isto, um `onerror` a seguir
     *    a um `onload` chamava o resto do caminho duas vezes.
     * 2. `timeout` -- o pedido morre ao fim de 12 segundos com um motivo. Um
     *    XHR sem prazo pode ficar pendurado para sempre, e foi o que aconteceu:
     *    nem resposta, nem erro, e o botao a cinzento sem nada que o acordasse.
     * 3. `try/catch` a toda a volta -- se rebentar antes de arrancar (ha
     *    extensoes que substituem o XMLHttpRequest), o erro chega ao caminho
     *    de falha em vez de matar a funcao a meio e deixar o botao preso.
     *
     * A regra: haja o que houver, `aoAcabar` e chamado exactamente uma vez. */
    var acabou = false;
    function terminar(erro, dados) {
      if (acabou) return;
      acabou = true;
      if (erro) console.warn('[ms-search]', metodo, url, '->', erro.message);
      else console.log('[ms-search]', metodo, url, '-> ok');
      aoAcabar(erro, dados);
    }

    /* Vai por `fetch` e nao por XHR, apesar de a Shopify o embrulhar.
     *
     * Cheguei a trocar para XHR a pensar que o embrulho dela partia o pedido
     * quando um bloqueador corta a telemetria. Nao parte: o pedido passa, e as
     * falhas da telemetria sao promessas soltas que nao contaminam nada.
     *
     * E o embrulho faz falta. E por ele que a loja fica a saber que o carrinho
     * mudou -- com XHR o artigo entrava e o contador do cabecalho continuava a
     * dizer zero, a loja a discordar de si propria na mesma pagina.
     *
     * O prazo fica, que esse resolvia um problema a serio: um pedido sem prazo
     * pode ficar pendurado e deixa o botao preso a cinzento para sempre. */
    var relogio = setTimeout(function () {
      terminar(new Error('sem resposta ao fim de 12s'), null);
    }, 12000);

    try {
      var opcoes = { method: metodo, headers: { 'Accept': 'application/json' } };
      if (corpo) {
        opcoes.headers['Content-Type'] = 'application/json';
        opcoes.body = JSON.stringify(corpo);
      }
      fetch(url, opcoes)
        .then(function (r) {
          return r.text().then(function (texto) {
            var j = null;
            try { j = JSON.parse(texto); } catch (e) { /* fica null */ }
            clearTimeout(relogio);
            terminar(r.ok ? null : new Error('HTTP ' + r.status + ' ' + texto.slice(0, 140)), j);
          });
        })
        .catch(function (e) {
          clearTimeout(relogio);
          terminar(new Error('pedido falhou: ' + (e && e.message)), null);
        });
    } catch (e) {
      clearTimeout(relogio);
      terminar(new Error('rebentou antes de enviar: ' + (e && e.message)), null);
    }
  }

  Camada.prototype.meterNoCarrinho = function (botao) {
    var self = this;
    var vid = botao.getAttribute('data-vid');
    if (!vid || botao.disabled) return;

    botao.disabled = true;
    botao.classList.add('is-a-meter');
    console.log('[ms-search] a meter no carrinho a variante', vid);

    function falhou(erro) {
      console.warn('[ms-search] nao consegui meter no carrinho:',
        (erro && erro.message) || 'motivo desconhecido');
      botao.classList.remove('is-a-meter');
      botao.disabled = false;

      /* Plano B: a ficha do produto, que e onde a loja sabe explicar o que se
         passou. Um botao que nao faz nada e pior do que um botao que leva a
         pessoa a outro sitio.
         O `closest` e de proposito: o pai directo do botao e o rodape, que so
         tem o preco. Estive a procurar o cartao no rodape, nao o encontrava, e
         o plano B nunca chegava a correr -- o erro acontecia em silencio. */
      var celula = botao.closest('.msc__celula');
      var cartao = celula && celula.querySelector('.msc__cartao');
      if (cartao) window.location.href = cartao.getAttribute('href');
    }

    pedir('POST', '/cart/add.js', { items: [{ id: Number(vid), quantity: 1 }] }, function (erro) {
      if (erro) { falhou(erro); return; }

      pedir('GET', '/cart.js', null, function (erro2, carrinho) {
        if (erro2 || !carrinho) { falhou(erro2 || new Error('carrinho ilegivel')); return; }

        self.mostrarCarrinho(carrinho);
        actualizarContadorDaLoja(carrinho);
        refazerGavetaDoTema();
        if (!self.pagina) fecharGavetaDoTema(self);
        botao.classList.remove('is-a-meter');
        botao.classList.add('is-dentro');
        // A marca de «ja esta» apaga-se sozinha: o cartao volta a poder ser
        // usado, que ha quem leve dois cabos iguais.
        setTimeout(function () {
          botao.classList.remove('is-dentro');
          botao.disabled = false;
        }, 1600);
      });
    });
  };

  /** Poe a barra a dizer o que ha no carrinho. Vazio, esconde-se. */
  Camada.prototype.mostrarCarrinho = function (carrinho) {
    if (!this.carrinho) return;
    var n = carrinho && carrinho.item_count ? carrinho.item_count : 0;
    if (!n) { this.carrinho.hidden = true; return; }
    var conta = this.carrinho.querySelector('.msc__carrinho-conta');
    if (conta) {
      conta.textContent = n + (n === 1 ? ' artigo' : ' artigos') + ' \u00b7 ' +
        formatarPreco((carrinho.total_price || 0) / 100, carrinho.currency || 'EUR');
    }
    this.carrinho.hidden = false;
  };

  /** O que ja la estava, ao abrir a camada. Falhando, fica escondida. */
  Camada.prototype.lerCarrinho = function () {
    var self = this;
    pedir('GET', '/cart.js', null, function (erro, c) {
      if (!erro && c) { self.mostrarCarrinho(c); actualizarContadorDaLoja(c); }
      /* Falhando, fica sem barra -- que e o estado de antes de haver barra. */
    });
  };

  Camada.prototype.desenharGrelha = function (visiveis) {
    if (!visiveis.length) {
      this.grelha.innerHTML = '';
      this.fim.innerHTML = this.estado.q
        ? '<p class="msc__vazio">Nada para <strong>' + escaparHtml(this.estado.q) + '</strong>' +
          ' com estes filtros.<br>Experimente menos palavras, uma marca, ou limpar os filtros.</p>'
        : (this.pagina
            ? '<p class="msc__vazio">Nenhum produto desta coleção com estes filtros.</p>'
            : '');
      return;
    }

    /* O cartão diz o mesmo que os cartões da loja, pela mesma ordem: etiqueta
       de desconto na imagem, marca, nome, estado do stock com a promessa de
       entrega, e o preço com o riscado ao lado. Um cartão nosso que mostrasse
       menos do que o do tema fazia a loja parecer dois sítios diferentes — e
       escondia a três dos argumentos de venda que ela já usa.

       O que não está cá é o número («1 em stock») e o «Quase esgotado !»: essa
       contagem só existe no servidor, não vem no ficheiro público de onde o
       índice se faz. Dizer «em stock» sem número é verdade; inventar o número
       não era. */
    var entrega = promessaDeEntrega();
    var mostrar = visiveis.slice(0, this.estado.mostrados);

    /* Onde acaba o que há e começa o que é sob consulta.
     *
     * Sem uma linha a dizê-lo, a queda parecia avaria: a grelha ia mostrando
     * preços e disponibilidade e a meio começava tudo a estar esgotado, sem
     * explicação nenhuma. Só se desenha quando as duas partes estão à vista. */
    var corte = -1;
    for (var n = 0; n < mostrar.length; n++) {
      if (!mostrar[n].doc.d) { corte = n; break; }
    }
    if (corte <= 0) corte = -1;

    this.grelha.innerHTML = mostrar.map(function (r, i) {
      var risca = i === corte
        ? '<span class="msc__corte">Temporariamente sem stock, fale conosco para encomendar</span>'
        : '';
      var d = r.doc;
      var img = d.i
        ? '<img class="msc__img" src="' + escaparHtml(imagemGrande(d.i)) + '" alt="" loading="lazy">'
        : '<span class="msc__img msc__img--vazia" aria-hidden="true"></span>';

      /* Um artigo de aluguer não é um artigo mais barato: é outro negócio.
         Quem procura «máquina de fumo» vê os dois lado a lado, e o preço de
         aluguer — mais baixo — parece uma pechincha até se perceber que é por
         dia. A etiqueta vem antes da do desconto porque muda o que o preço
         quer dizer, e um artigo pode ter as duas.

         O `r` vem do indexador, que é quem vê as tags. Não o havendo — índice
         anterior a isto — fica-se pelo título, que apanha a maior parte. */
      var etiquetas = '';
      if (d.r === 1 || normalizar(d.t).indexOf('aluguer') !== -1) {
        etiquetas += '<span class="msc__etiqueta msc__etiqueta--aluguer">Aluguer</span>';
      }

      /* «Vendido ao metro» muda o que o preço quer dizer, tal como o aluguer:
         1,69 € não é o cabo, é cada metro dele. Quem não repara nisso na
         grelha repara na factura, e aí é tarde para toda a gente.

         Vem logo a seguir ao aluguer e pela mesma razão: são as duas
         etiquetas que avisam que o número não quer dizer o que parece. */
      if (/\bao\s+metro\b/i.test(d.t)) {
        etiquetas += '<span class="msc__etiqueta msc__etiqueta--metro">Vendido ao metro</span>';
      }
      /* Um euro de minimo. A loja tem 4.767 artigos com preco de comparacao
         acima do preco, e 962 desses poupam centimos: sao os 4,95 que passaram
         a 4,90, convencao de preco e nao promocao. Uma etiqueta vermelha a
         dizer «Desc. -0,06 €» nao anuncia nada, parece um erro.

         O minimo e em dinheiro e nao em percentagem de proposito: a etiqueta
         mostra euros, e um limite de 2% escondia 19,10 EUR num subwoofer de
         999 e 115 EUR num projector de 3.840 -- desconto a serio -- para so
         apanhar mais 18 artigos alem dos que o euro ja apanha.

         Vale para o cartao inteiro: sem etiqueta tambem nao ha preco riscado,
         que um 4,95 cortado por cima de um 4,90 diz a mesma coisa. */
      var poupanca = (d.pc && d.pc > d.p) ? d.pc - d.p : 0;
      var emSaldo = poupanca >= DESCONTO_MINIMO;

      if (emSaldo) {
        etiquetas += '<span class="msc__etiqueta msc__etiqueta--desconto">Desc. -' +
          formatarPreco(poupanca, d.c) + '</span>';
      }

      /* A segunda linha do cartão.
       *
       * Um aluguer NUNCA promete entrega. A promessa vem do
       * `window.BUKPROD_ENTREGA()`, que é uma só para a página inteira e não
       * sabe distinguir um cabo de um aluguer -- prometia «receba na segunda»
       * a material que se levanta, se combina e se devolve. Prometer o que a
       * loja não faz é pior do que não prometer nada.
       *
       * Sem visto. Era decoração por cima de texto que já dizia uma coisa boa,
       * e uma terceira marca verde na mesma esquina do cartão. */
      var aviso = entrega && !d.r ? '<span class="msc__entrega">' + escaparHtml(entrega) + '</span>' : '';

      /* Quantas unidades há, e em que tom.
       *
       * «Em stock» responde «posso comprar?». O número responde «posso
       * comprar os que preciso?», que numa loja de cabos e fichas é metade
       * das perguntas: quem vem buscar dez fichas XLR quer saber se há dez.
       *
       * Uma ou duas unidades mudam de cor porque mudam de significado: deixa
       * de ser «há» e passa a ser «há agora». É informação verdadeira, e
       * não um contador inventado — sai do stock real da Shopify.
       *
       * O número passa por Math.floor e nunca por escaparHtml. Não é
       * distração: forçar a número garante que nada do índice pode entrar
       * nesta linha como marcação, sem depender de eu me lembrar de escapar.
       *
       * Sem número — variante que não conta stock, ou índice gerado antes de
       * isto existir — fica o «Em stock» de sempre. */
      var unidades = Math.floor(d.q > 0 ? d.q : 0);

      /* Um cabo ao metro nao tem 66 unidades, tem 66 metros. E a mesma regra
         que ja decide a etiqueta verde la em cima, e tem de ser a mesma: dizer
         <<vendido ao metro>> num canto e <<66 unidades>> no outro fazia a
         pessoa perguntar-se qual das duas esta errada. */
      var aoMetro = /\bao\s+metro\b/i.test(d.t || '');

      /* O amarelo e so para a ultima unidade.
       *
       * Comecou por ser para uma OU duas, e medi o que isso dava nas pesquisas
       * reais: 40% dos cartoes em <<cabo>>, 46% em <<coluna>>, 56% em
       * <<microfone>>. Um aviso que aparece em metade do ecra deixa de ser
       * aviso e passa a ser fundo. So com a unidade unica cai para 13%, e volta
       * a querer dizer alguma coisa.
       *
       * Duas unidades continuam a aparecer, com o numero, mas em verde: e
       * informacao boa, nao e urgencia. */
      var emStock;
      if (unidades === 1) {
        emStock = '<span class="msc__stock msc__stock--poucas">' +
          (aoMetro ? '\u00daltimo metro em stock!' : '\u00daltima unidade em stock!') + '</span>';
      } else if (unidades >= 2) {
        emStock = '<span class="msc__stock">Em stock \u00b7 ' + unidades +
          (aoMetro ? ' metros' : ' unidades') + '</span>';
      } else {
        emStock = '<span class="msc__stock">Em stock</span>';
      }

      /* Um aluguer fica como no cartão do tema: marca, nome e preço, sem linha
         de stock. O «Contacte-nos» que esteve aqui não dizia nada a quem olha
         para a grelha (César, 17/10/2026) -- quem quer alugar abre a ficha. */
      var stock = d.r
        ? ''
        : d.d
          ? emStock + aviso
          : // «Sem stock» e nao «sob consulta»: e o que a ficha do produto diz, e duas
          // palavras diferentes para o mesmo estado fazem a loja parecer dois sitios.
          '<span class="msc__stock msc__stock--nao">Sem stock</span>';

      var precos = (emSaldo
          ? '<s class="msc__preco-velho">' + formatarPreco(d.pc, d.c) + '</s> '
          : '') +
        '<span class="msc__preco-cartao">' + formatarPreco(d.p, d.c) + '</span>';

      /* O botao de compra fica FORA do <a>.
       *
       * Um <button> dentro de uma ligacao e marcacao invalida, e pior do que
       * isso: no telemovel o dedo acerta nos dois e ninguem sabe qual e que
       * ganha. Por isso o cartao passa a ter uma caixa a volta, com a ligacao
       * e o botao como irmaos.
       *
       * So aparece quando ha variante para meter no carrinho. O `vid` vem do
       * indice e so la esta quando a variante que manda no preco mostrado
       * esta disponivel -- o que garante que o preco do cartao e o preco que
       * vai para o carrinho.
       *
       * Nunca em alugueres. Um aluguer precisa de datas e de caucao; entrar no
       * carrinho com um toque e criar uma encomenda que alguem vai ter de
       * desfazer ao telefone. Esses levam a pessoa a ficha, como ate aqui. */
      var comprar = (d.vid && !d.r)
        ? '<button type="button" class="msc__comprar" data-msc="comprar"' +
          atr('data-vid', String(d.vid)) + atr('data-nome', d.t) +
          ' aria-label="Adicionar ao carrinho">' + ICONE_CARRINHO + '</button>'
        : '';

      /* A imagem numa caixa propria, com o botao por cima da esquina.
       *
       * A caixa existe para o `position: absolute` do botao ter a que se
       * agarrar: ela tem exactamente o tamanho da moldura, que e quadrada, por
       * isso `bottom: 8px` cai onde a imagem acaba. Sem ela eu teria de
       * adivinhar a altura da imagem com um numero -- foi o que fiz a primeira
       * vez, com `padding` no cartao inteiro, e encolhi todas as fotografias.
       *
       * Sao duas ligacoes para a mesma ficha: uma na imagem, outra no texto.
       * A da imagem leva `aria-hidden` e `tabindex="-1"` para quem navega por
       * teclado ou ouve a pagina nao levar o mesmo link duas vezes seguidas. */
      var endereco = '/products/' + escaparHtml(d.h);

      return risca +
        '<div class="msc__celula">' +
          '<div class="msc__imagem">' +
            '<a class="msc__ir" href="' + endereco + '"' + atr('data-handle', d.h) +
            ' tabindex="-1" aria-hidden="true">' +
              '<span class="msc__moldura">' + img +
              (etiquetas ? '<span class="msc__etiquetas">' + etiquetas + '</span>' : '') +
              '</span>' +
            '</a>' +
            comprar +
          '</div>' +
          '<a class="msc__cartao" href="' + endereco + '"' + atr('data-handle', d.h) + '>' +
            '<span class="msc__marca">' + escaparHtml(d.v) + '</span>' +
            '<span class="msc__nome">' + escaparHtml(d.t) + '</span>' +
            stock +
            '<span class="msc__precos">' + precos + '</span>' +
          '</a>' +
        '</div>';
    }).join('');

    var faltam = visiveis.length - mostrar.length;
    this.fim.innerHTML = faltam > 0
      ? '<button type="button" class="msc__mais-produtos" data-msc="mais-produtos">' +
        'Mostrar mais ' + Math.min(faltam, PAGINA) + ' <span>(faltam ' + faltam + ')</span></button>'
      : (this.estado.q
          ? '<a class="msc__nativa" href="/search?q=' + encodeURIComponent(this.estado.q) + '">' +
            'Ver na pesquisa da loja</a>'
          : '');
  };

  /**
   * Conta ao bulk o que foi procurado e o que isso deu.
   *
   * Só a pergunta e o número de resultados — nada sobre quem procurou. E só
   * quando a pessoa pára de escrever: sem isto, "coluna" ficava registada seis
   * vezes, uma por letra, e a lista não valia nada.
   *
   * Falhar aqui não pode ter consequência nenhuma para quem está a pesquisar.
   */
  Camada.prototype.registar = function (query, resultados, handle) {
    if (!this.cfg.registo || !query) return;
    var corpo = JSON.stringify({ q: query, resultados: resultados, clicou: handle || null });
    try {
      if (navigator.sendBeacon) {
        // text/plain e não application/json: para outro domínio o Chrome recusa
        // o beacon JSON sem dar erro, e de 05/09 a 14/10 só se registaram 2.
        navigator.sendBeacon(this.cfg.registo, new Blob([corpo], { type: 'text/plain' }));
      } else {
        fetch(this.cfg.registo, {
          method: 'POST',
          body: corpo,
          headers: { 'Content-Type': 'application/json' },
          keepalive: true,
        });
      }
    } catch (e) { /* o registo é acessório; a pesquisa não pode sofrer com ele */ }
  };

  Camada.prototype.agendarRegisto = function (query, resultados) {
    var self = this;
    clearTimeout(this.timerRegisto);
    if (query === this.ultimaRegistada) return;
    this.timerRegisto = setTimeout(function () {
      self.ultimaRegistada = query;
      self.registar(query, resultados, null);
    }, 1400);
  };

  /* ------------------------------------------------------------- adopção */

  var MS = { Motor: Motor, Camada: Camada, adoptar: adoptar, arrancar: arrancar, juntarStock: juntarStock,
    montarColecao: montarColecao, camada: null, colecao: null };
  window.MSSearch = MS;

  /**
   * Modo "adopção": em vez de substituir o bloco de pesquisa do tema, agarra a
   * caixa que já lá está. Evita mexer no header.liquid — que no Xtra tem 140 KB
   * e é reescrito a cada actualização do tema — e mantém o layout e o
   * comportamento em telemóvel tal como estão.
   *
   * Configurar antes de carregar este ficheiro:
   *   window.MS_SEARCH_CONFIG = { indice: '...', input: '#search_main' };
   *
   * Só se toma conta da caixa depois de o índice estar carregado e válido. Ao
   * contrário: se o índice falhasse — CDN em baixo, asset por publicar, rede do
   * cliente a cair — o input já tinha sido substituído e ficava sem pesquisa
   * nenhuma, nem a nossa nem a do tema. Assim, o pior que acontece é a loja
   * continuar exactamente como está hoje.
   */
  /**
   * O stock de agora, de todos os produtos, gerado pelo bulk a seguir a cada
   * import de stock (e de hora a hora). O índice é uma fotografia do dia em que o
   * indexador correu; isto é o que muda todos os dias — e assim muda sem publicar
   * o tema. O endereço vem da configuração; num snippet antigo, sem `stock`,
   * deduz-se do `registo`, que vive no mesmo sítio.
   */
  function pedirStock(cfg) {
    var url = cfg.stock || (cfg.registo ? String(cfg.registo).replace(/\/consulta$/, '/stock') : null);
    if (!url || typeof fetch !== 'function') return Promise.resolve(null);
    var tempo = new Promise(function (ok) { setTimeout(function () { ok(null); }, 4000); });
    // Sem cache do browser (16/10/2026): cada visita leva o stock de agora.
    var pedido = fetch(url, { cache: 'no-store' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
    return Promise.race([pedido, tempo]);
  }

  /* Sobrepõe o stock de agora ao índice: {handle: [disponível, unidades, preço,
   * preço antes, variante]}. Os produtos que já não estão activos saem (com o
   * ficheiro completo, para um ficheiro partido nunca esvaziar a pesquisa), e os
   * criados depois do índice entram, só com título, marca e referência. */
  function juntarStock(json, st) {
    if (!json || !json.produtos || !st || !st.p) return;
    var por = st.p;
    var completo = (st.n || 0) > json.produtos.length * 0.8;
    var vistos = Object.create(null);
    json.produtos = json.produtos.filter(function (d) {
      vistos[d.h] = 1;
      var e = por[d.h];
      if (!e) return !completo;
      d.d = !!e[0];
      d.q = e[0] ? (e[1] != null ? e[1] : (d.q || 0)) : 0;
      d.p = e[2];
      if (e[3]) d.pc = e[3]; else delete d.pc;
      if (e[4]) d.vid = e[4]; else delete d.vid;
      return true;
    });
    (st.novos || []).forEach(function (n) {
      if (!vistos[n.h]) { vistos[n.h] = 1; json.produtos.push(n); }
    });
    var vendas = st.v || null;
    var recentes = Object.create(null);
    (st.rec || []).forEach(function (h) { recentes[h] = 1; });
    json.produtos.forEach(function (d) {
      if (vendas) d.uv = vendas[d.h] || 0;
      if (recentes[d.h]) d.nv = 1;
    });
    if (st.cl) json.cliques = st.cl;
  }

  /**
   * O índice e o stock, carregados uma vez por página.
   *
   * A caixa de pesquisa e a página de coleção pedem o mesmo motor: numa coleção
   * ele já vem carregado quando a pessoa toca na lupa, e a camada abre sem
   * esperar. Falhando, esquece-se a promessa, para a tentativa seguinte poder
   * tentar outra vez em vez de herdar a falha.
   */
  var motorPedido = null;

  function carregarMotor(cfg) {
    if (motorPedido) return motorPedido;
    // O stock vem à parte, do bulk, e em paralelo com o índice. Falhando, fica
    // o que o índice diz — a pesquisa nunca fica à espera dele.
    var stock = pedirStock(cfg);
    motorPedido = fetch(cfg.indice, { cache: 'force-cache' })
      .then(function (r) {
        if (!r.ok) throw new Error('índice indisponível (' + r.status + ')');
        return r.json();
      })
      .then(function (json) {
        return stock.then(function (st) { juntarStock(json, st); return json; });
      })
      .then(function (json) {
        if (!json || !json.produtos || !json.produtos.length) throw new Error('índice vazio');
        return new Motor(json);
      });
    motorPedido.catch(function () { motorPedido = null; });
    return motorPedido;
  }

  function adoptar(cfg) {
    var original = document.querySelector(cfg.input || 'input[name="q"]');
    if (!original || original.__msPronto) return;
    original.__msPronto = true;

    var aTentar = false;
    var iniciar = function () {
      if (aTentar) return;
      aTentar = true;
      carregarMotor(cfg)
        .then(function (motor) {
          original.removeEventListener('focus', iniciar);
          original.removeEventListener('pointerenter', iniciar);
          instalar(cfg, original, motor);
        })
        .catch(function (erro) {
          console.warn('[ms-search]', erro.message, '— fica a pesquisa do tema.');
          aTentar = false;
        });
    };

    original.addEventListener('focus', iniciar);
    original.addEventListener('pointerenter', iniciar);
  }

  function instalar(cfg, original, motor) {
    // Um clone não traz os ouvintes do tema — é a forma de desligar a pesquisa
    // instantânea nativa sem lhe tocar no código.
    var input = original.cloneNode(true);
    var estavaFocado = document.activeElement === original;
    var jaEscrito = original.value;
    original.parentNode.replaceChild(input, original);
    input.setAttribute('autocomplete', 'off');
    if (cfg.placeholder) input.setAttribute('placeholder', cfg.placeholder);

    // O painel do tema, se existir, fica fechado: quem mostra resultados agora
    // é a camada, e dois sítios a responder à mesma pergunta é um a mais.
    if (cfg.painel) {
      var antigo = document.querySelector(cfg.painel);
      if (antigo) { antigo.hidden = true; antigo.innerHTML = ''; }
    }

    var camada = new Camada(cfg, motor);
    camada.campoDoTema = input;
    MS.camada = camada;

    var abrir = function () { camada.abrir(input.value); };
    input.addEventListener('focus', abrir);
    input.addEventListener('click', abrir);

    // Escrever na caixa do tema procura na camada. É o mesmo atraso que a caixa
    // de dentro usa: os dois campos são o mesmo campo, só que um está no
    // cabeçalho da loja e o outro aparece quando aquele não está à vista.
    var espera = null;
    input.addEventListener('input', function () {
      if (!camada.aberta) camada.abrir(input.value);
      clearTimeout(espera);
      espera = setTimeout(function () { camada.procurar(input.value); }, DEBOUNCE_MS);
    });

    // A caixa do tema deixa de recarregar para /search: a camada responde
    // melhor e sem sair da página. Lá dentro continua a haver saída para a
    // pesquisa nativa, para quem a quiser.
    var forma = input.closest('form');
    if (forma) {
      forma.addEventListener('submit', function (e) {
        e.preventDefault();
        camada.abrir(input.value);
      });
    }

    window.addEventListener('popstate', function () {
      if (camada.aberta) camada.fechar(true);
    });

    // Quem já estava com o dedo na caixa à espera do índice não tem de lá voltar.
    if (estavaFocado || jaEscrito) camada.abrir(jaEscrito);
  }

  /* ------------------------------------------------------ páginas de coleção */

  /* A ordem que vem no endereço, dos links de ordenação do tema. */
  var ORDEM_DO_TEMA = {
    'price-ascending': 'preco-asc',
    'price-descending': 'preco-desc',
    'title-ascending': 'nome',
  };

  /* Que produtos da coleção, e por que ordem. O bulk sabe; o índice não. */
  function pedirColecao(url) {
    var tempo = new Promise(function (ok) { setTimeout(function () { ok(null); }, 6000); });
    var pedido = fetch(url)
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
    return Promise.race([pedido, tempo]);
  }

  /* A base da grelha: os documentos da coleção, já com a pontuação que a
     ordenação «Em destaque» vai respeitar.

     Com uma lista, é a ordem da coleção -- manual, mais vendidos, A-Z, o que
     estiver escolhido no admin. Uma coleção que é o catálogo inteiro vem sem
     lista (`todos`), e aí a ordem é a dos mais vendidos, como nos destaques da
     camada. */
  function baseDaColecao(motor, dados) {
    var docs = motor.docs;
    if (dados.todos) {
      return docs.map(function (d) {
        var escalao = d.uv !== undefined ? d.uv : (d.u || 0) * 2;
        return { doc: d, score: escalao + (d.nv ? 1 : 0) };
      });
    }
    var porHandle = Object.create(null);
    docs.forEach(function (d) { porHandle[d.h] = d; });
    var lista = dados.h || [];
    var base = [];
    for (var i = 0; i < lista.length; i++) {
      var d = porHandle[lista[i]];
      if (d) base.push({ doc: d, score: lista.length - i });
    }
    return base;
  }

  /**
   * A página de uma coleção desenhada pela pesquisa.
   *
   * Mesma grelha, cartões, atalhos, facetas e ordenação que a camada, tirados
   * do mesmo índice -- filtrar e ordenar passa a ser instantâneo, sem recarregar
   * a página como a grelha do tema.
   *
   * Até tudo estar pronto, a grelha do tema fica à vista e a funcionar. Não
   * havendo índice, lista da coleção, ou o sítio onde a pôr, fica ela -- é o
   * mesmo princípio da caixa de pesquisa: o pior caso é a loja como estava.
   *
   * Não se toma conta quando o endereço traz filtros do tema (`filter.*`): quem
   * chegou por um desses links pediu uma coisa que a lista não sabe fazer.
   */
  /* O bloco do BukProd no <head> esconde a grelha do tema nas coleções
     (classe `msc-col-espera`), para ela não aparecer e ser trocada logo a
     seguir. Tira-se assim que se sabe o que fica: a nossa ou a dele. O <head>
     tira-a sozinho aos 6 s, se este ficheiro nunca chegar a correr. */
  function largarEspera() {
    document.documentElement.classList.remove('msc-col-espera');
  }

  function montarColecao(cfg) {
    var col = cfg.colecao;
    if (!col || !col.handle || !col.lista || typeof fetch !== 'function') { largarEspera(); return; }
    var embrulho = document.querySelector('.collection-wrapper[data-standard-collection-handle]');
    if (!embrulho || embrulho.__msPronto) { largarEspera(); return; }
    embrulho.__msPronto = true;

    var params = new URLSearchParams(window.location.search);
    var comFiltros = false;
    params.forEach(function (v, k) {
      if (k.indexOf('filter.') === 0 && k !== 'filter.v.availability') comFiltros = true;
    });
    if (comFiltros) { largarEspera(); return; }

    Promise.all([carregarMotor(cfg), pedirColecao(col.lista)])
      .then(function (r) {
        if (!r[1]) throw new Error('lista da coleção indisponível');
        var base = baseDaColecao(r[0], r[1]);
        if (!base.length) throw new Error('coleção sem produtos no índice');
        var camada = new Camada(cfg, r[0], {
          embrulho: embrulho,
          handle: col.handle,
          ordem: ORDEM_DO_TEMA[params.get('sort_by')] || null,
        });
        camada.mostrarColecao(base);
        MS.colecao = camada;
        largarEspera();
        var motor = r[0];
        (window.requestIdleCallback || function (f) { setTimeout(f, 300); })(function () { motor.preparar(); });
      })
      .catch(function (erro) {
        console.warn('[ms-search] coleção:', erro.message, '— fica a grelha do tema.');
        largarEspera();
      });
  }

  function arrancar() {
    if (window.MS_SEARCH_CONFIG && window.MS_SEARCH_CONFIG.indice) {
      adoptar(window.MS_SEARCH_CONFIG);
      montarColecao(window.MS_SEARCH_CONFIG);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arrancar);
  } else {
    arrancar();
  }
})();
